import * as THREE from 'three';
import { addSurfaceDetail } from './Surface';
import { CELL, FENCE_INSET, type CityMap } from '../game/map';
import type { Theme } from '../levels/types';
import { createSeededRandom } from '../utils/random';
import { Batcher } from './Batcher';
import { LightSystem, type LightSource, type LightState } from './LightSystem';
import type { PropKind, PropSpot } from './Props';

export type Blocker = { x: number; z: number; r: number };

export type Shutter = { mesh: THREE.Mesh; open: number; target: number };

export type TrainState = 'none' | 'coming' | 'passing' | 'gone';

/** The level crossing on a night's railway line ('K' cells on an 'r' row). */
export type Crossing = {
  cells: { c: number; r: number }[];
  /** Flashing red lamps (and the bell, which the game plays). */
  setWarning: (on: boolean) => void;
  setGates: (down: boolean) => void;
  /** Send a train through along the track row. */
  runTrain: () => void;
  readonly train: TrainState;
};

/** Where a crow can sit: on a wire, a pole's crossarm, or the ground by the rubbish. */
export type Perch = { x: number; y: number; z: number; rot: number; ground: boolean };

export type WorldBuild = {
  group: THREE.Group;
  /** Street clutter from the prop models; drawn once they have loaded (see Props.ts). */
  props: PropSpot[];
  perches: Perch[];
  lights: LightSystem;
  blockers: Blocker[];
  vendingSpots: THREE.Vector3[];
  /** Traffic mirror positions and the direction each faces. */
  mirrors: { pos: THREE.Vector3; facing: THREE.Vector3; ghost: THREE.Mesh }[];
  /** Roll down the shop shutter for light `Q-c-r`. */
  closeShutter: (id: string) => void;
  /** Animate moving parts (shutters, crossing gates, the train, candles). */
  update: (dt: number) => void;
  crossing: Crossing | null;
  /** Back to the start of the night: gates up, no train, power back on. */
  reset: () => void;
  /**
   * Power cut, sweeping out from (x, z): street lights, shops and windows die;
   * candles ('K-' lights) come out a few seconds later.
   */
  blackout: (x: number, z: number) => void;
  dispose: () => void;
};

const ARCADE = new Set(['R', 'Y', 'W', 'Q']);

type Side = { dc: number; dr: number; rot: number };
const SIDES: Side[] = [
  { dc: 0, dr: -1, rot: Math.PI }, // north face
  { dc: 0, dr: 1, rot: 0 }, // south face
  { dc: 1, dr: 0, rot: Math.PI / 2 }, // east face
  { dc: -1, dr: 0, rot: -Math.PI / 2 }, // west face
];

const WALL_COLORS = ['#a39d91', '#b4ab9b', '#8f8b84', '#c2b8a6', '#9a9184', '#7d8079', '#a8a197', '#b9b2a4', '#8a7f72'];
const ROOF_COLORS = ['#2b2f36', '#33302d', '#2a3340', '#3a2f2a', '#26292b', '#40403c'];
const WINDOW_LIT = ['#ffc27a', '#ffd9a0', '#ffe7c2', '#ffb870', '#9fb8ff', '#ffcf8f'];
const BLOCK_WALL = ['#7f7d76', '#8b8880', '#76736b', '#918c82'];

type Rng = () => number;

export class WorldBuilder {
  private readonly rng: Rng = createSeededRandom(20231117);
  /** Decoration only (frames, laundry, bikes…): never feeds back into the layout. */
  private readonly deco: Rng = createSeededRandom(4417);
  /** Clutter and small details have their own stream, so adding them never reshuffles the buildings. */
  private readonly clutter: Rng = createSeededRandom(5099);
  private readonly props: PropSpot[] = [];
  private readonly perches: Perch[] = [];
  private readonly batch: Batcher;
  private readonly lights = new LightSystem();
  private readonly group = new THREE.Group();
  private readonly blockers: Blocker[] = [];
  private readonly vendingSpots: THREE.Vector3[] = [];
  private readonly disposables: { dispose: () => void }[] = [];
  private readonly custom = new Set<string>();
  private readonly poles: { x: number; z: number; alongX: boolean }[] = [];
  private readonly shutters = new Map<string, Shutter>();
  private readonly mirrors: WorldBuild['mirrors'] = [];
  private readonly snow: boolean;
  private readonly litWindow: THREE.MeshBasicMaterial;
  private crossing: Crossing | null = null;
  private crossingUpdate: ((dt: number) => void) | null = null;
  private crossingReset: (() => void) | null = null;
  private readonly pendingLights: { at: number; light: LightSource; state: LightState }[] = [];
  private windowFade: { material: THREE.MeshBasicMaterial; t: number } | null = null;
  private clock = 0;

  private readonly box = new THREE.BoxGeometry(1, 1, 1);
  private readonly plane = new THREE.PlaneGeometry(1, 1);
  private readonly cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
  private readonly cone = new THREE.ConeGeometry(0.5, 1, 9);
  private readonly prism = new THREE.CylinderGeometry(1, 1, 1, 3);
  private readonly sphere = new THREE.SphereGeometry(0.5, 12, 8);
  /** Bamboo: six-sided culms and flat sprays of narrow leaves (groves have hundreds of each). */
  private readonly culm = new THREE.CylinderGeometry(0.5, 0.5, 1, 6);
  private readonly spray = createLeafSpray();
  /** Lots of bamboo (a bamboo maze): thinner groves, so the night stays fast. */
  private groveLean = false;
  /** A single flat leaf (non-indexed, to batch with the sprays). */
  private readonly leafQuad = new THREE.PlaneGeometry(1, 1).toNonIndexed();

  private readonly wallColors: string[];
  private readonly roofColors: string[];
  private readonly asphalt: string;
  private readonly wet: boolean;
  private readonly overcast: boolean;
  /** Brightness of lit windows: a warm glow at night, barely visible by day (and never blooming). */
  private readonly windowGlow: number;
  /** Daytime: street lamps (and the sensor light) stay off. */
  private readonly daylight: boolean;

  constructor(
    private readonly map: CityMap,
    theme?: Theme,
  ) {
    this.wallColors = theme?.walls ?? WALL_COLORS;
    this.roofColors = theme?.roofs ?? ROOF_COLORS;
    this.asphalt = theme?.asphalt ?? '#2b2c2f';
    this.wet = !!theme?.wet;
    this.overcast = !!theme?.overcast || !!theme?.daylight;
    this.snow = !!theme?.snow;
    this.windowGlow = theme?.daylight ? 0.28 : 1.1;
    this.daylight = !!theme?.daylight;
    const std = (roughness: number, metalness = 0) =>
      new THREE.MeshStandardMaterial({ vertexColors: true, roughness, metalness });
    const litWindow = new THREE.MeshBasicMaterial({ vertexColors: true });
    litWindow.color.setScalar(this.windowGlow);
    this.litWindow = litWindow;
    const glow = new THREE.MeshBasicMaterial({ vertexColors: true });
    glow.color.setScalar(2.2);
    const materials: Record<string, THREE.Material> = {
      wall: std(0.93),
      roof: std(0.68, 0.15),
      concrete: std(0.96),
      metal: std(0.45, 0.55),
      glass: std(0.18, 0.7),
      litWindow,
      glow,
      foliage: std(1),
      // Thin leaf blades, seen from both sides.
      leaf: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.75, side: THREE.DoubleSide }),
      wood: std(0.9),
      water: std(0.08, 0.5),
    };
    for (const m of Object.values(materials)) this.disposables.push(m);
    addSurfaceDetail(materials.wall as THREE.MeshStandardMaterial, 'wall');
    addSurfaceDetail(materials.concrete as THREE.MeshStandardMaterial, 'wall');
    addSurfaceDetail(materials.roof as THREE.MeshStandardMaterial, 'roof');
    this.batch = new Batcher(materials);
    this.disposables.push(this.box, this.plane, this.cyl, this.cone, this.prism, this.sphere, this.culm, this.spray, this.leafQuad);
  }

  build(): WorldBuild {
    this.groveLean = this.map.find('j').length > 40;
    this.markCustomCells();
    this.buildGround();
    this.buildBlocks();
    this.buildStreetFurniture();
    this.buildKonbini();
    this.buildStation();
    this.buildMaisonSakura();
    this.buildKomorebiSo();
    this.buildShrine();
    this.buildRoadWorks();
    this.buildArcade();
    this.buildWater();
    this.buildBenches();
    this.buildRailway();
    this.buildUnderpass();
    this.buildCandles();
    this.buildWires();
    this.buildClutter();
    this.buildSky();

    const statics = this.batch.build(
      new Set(['wall', 'roof', 'concrete', 'metal', 'foliage', 'wood', 'leaf']),
      new Set(['wall', 'roof', 'concrete', 'metal', 'foliage', 'wood', 'leaf']),
    );
    this.group.add(statics);
    this.group.add(this.lights.group);
    for (const child of statics.children) {
      const mesh = child as THREE.Mesh;
      this.disposables.push(mesh.geometry);
    }

    return {
      group: this.group,
      props: this.props,
      perches: this.perches,
      lights: this.lights,
      blockers: this.blockers,
      vendingSpots: this.vendingSpots,
      mirrors: this.mirrors,
      crossing: this.crossing,
      reset: () => {
        this.pendingLights.length = 0;
        this.windowFade = null;
        this.litWindow.color.setScalar(this.windowGlow);
        this.crossingReset?.();
      },
      blackout: (x: number, z: number) => this.blackout(x, z),
      closeShutter: (id: string) => {
        const sh = this.shutters.get(id);
        if (sh) sh.target = 0;
      },
      update: (dt: number) => {
        this.clock += dt;
        this.crossingUpdate?.(dt);
        for (let i = this.pendingLights.length - 1; i >= 0; i -= 1) {
          const p = this.pendingLights[i];
          if (this.clock < p.at) continue;
          p.light.setState(p.state);
          this.pendingLights.splice(i, 1);
        }
        if (this.windowFade) {
          this.windowFade.t = Math.max(0, this.windowFade.t - dt * 0.8);
          this.windowFade.material.color.setScalar(0.04 + this.windowFade.t * 1.06);
        }
        for (const sh of this.shutters.values()) {
          if (Math.abs(sh.open - sh.target) < 0.001) continue;
          sh.open += Math.sign(sh.target - sh.open) * Math.min(Math.abs(sh.target - sh.open), dt * 0.9);
          // 1 = rolled up out of sight, 0 = down to the floor.
          sh.mesh.scale.y = Math.max(0.02, 3 * (1 - sh.open));
          sh.mesh.position.y = 3.2 - (3 * (1 - sh.open)) / 2;
        }
      },
      dispose: () => {
        for (const d of this.disposables) d.dispose();
        this.lights.dispose();
      },
    };
  }

  // ---------------------------------------------------------------- helpers

  private key(c: number, r: number): string {
    return `${c},${r}`;
  }

  private pick<T>(list: T[]): T {
    return list[Math.floor(this.rng() * list.length) % list.length];
  }

  private glowMaterial(color: THREE.ColorRepresentation, scale = 3): THREE.MeshBasicMaterial {
    const m = new THREE.MeshBasicMaterial({ color });
    m.color.multiplyScalar(scale);
    this.disposables.push(m);
    return m;
  }

  private texturedMesh(
    texture: THREE.Texture,
    w: number,
    h: number,
    emissiveScale: number,
    basic = true,
  ): THREE.Mesh {
    const material = basic
      ? new THREE.MeshBasicMaterial({ map: texture, transparent: true })
      : new THREE.MeshStandardMaterial({ map: texture, roughness: 0.8 });
    if (basic) (material as THREE.MeshBasicMaterial).color.setScalar(emissiveScale);
    this.disposables.push(material, texture);
    const mesh = new THREE.Mesh(this.plane, material);
    mesh.scale.set(w, h, 1);
    return mesh;
  }

  private addLight(options: {
    id: string;
    lamp: THREE.Vector3;
    ground: THREE.Vector2;
    radius: number;
    color: THREE.ColorRepresentation;
    intensity: number;
    state?: LightState;
    exposure?: number;
    cone?: boolean;
  }): LightSource {
    return this.lights.add({ ...options, pool: true });
  }

  // ---------------------------------------------------------------- layout

  private markCustomCells(): void {
    // Buildings that are built by hand around their legend character.
    const mark = (cells: { c: number; r: number }[], offsets: [number, number][]) => {
      for (const { c, r } of cells) for (const [dc, dr] of offsets) this.custom.add(this.key(c + dc, r + dr));
    };
    const station: [number, number][] = [];
    for (let dc = -4; dc <= 4; dc += 1) station.push([dc, 0], [dc, 1]);
    mark(this.map.find('E'), station);
    // Lit entrance that isn't hers: the block east of the alcove.
    mark(this.map.find('A'), [[0, -1], [1, -1], [2, -1], [0, 1], [1, 1], [2, 1], [1, 0], [2, 0]]);
    // Her apartment block, north of the entrance alcove.
    mark(this.map.find('H'), [[-1, -1], [0, -1], [1, -1], [-1, 0], [1, 0], [-1, -2], [0, -2], [1, -2]]);
    mark(this.map.find('C'), [[0, 0]]);
  }


  private buildGround(): void {
    const px = 64;
    const map = this.map;
    const canvas = document.createElement('canvas');
    canvas.width = map.width * px;
    canvas.height = map.height * px;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas unavailable');
    const rng = createSeededRandom(77);

    ctx.fillStyle = '#16150f';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const inset = ((FENCE_INSET + 0.32) / CELL) * px;
    for (let r = 0; r < map.height; r += 1) {
      for (let c = 0; c < map.width; c += 1) {
        const x = c * px;
        const y = r * px;
        const ch = map.char(c, r);
        if (ch === 'r' || ch === 'K') {
          // Ballast and sleepers (the crossing cell gets asphalt between the rails).
          ctx.fillStyle = ch === 'K' ? this.asphalt : '#2a2824';
          ctx.fillRect(x, y, px, px);
          if (ch === 'r') {
            for (let i = 0; i < 160; i += 1) {
              ctx.fillStyle = rng() < 0.5 ? 'rgba(110,104,96,0.5)' : 'rgba(8,8,8,0.5)';
              ctx.fillRect(x + rng() * px, y + rng() * px, 1.5, 1.5);
            }
            ctx.fillStyle = '#3b2f26';
            for (let k = 2; k < px; k += 8) ctx.fillRect(x + k, y + px * 0.3, 4, px * 0.4);
          } else {
            // Yellow-and-black stripes either side of the tracks.
            for (let k = 0; k < px; k += 8) {
              ctx.fillStyle = (k / 8) % 2 === 0 ? '#c9a227' : '#151515';
              ctx.fillRect(x + k, y + 2, 8, 4);
              ctx.fillRect(x + k, y + px - 6, 8, 4);
            }
          }
          ctx.fillStyle = '#8d8f93';
          ctx.fillRect(x, y + px * 0.38, px, 2);
          ctx.fillRect(x, y + px * 0.62 - 2, px, 2);
          continue;
        }
        if (!map.isStreet(c, r)) {
          ctx.fillStyle = ch === 'w' ? '#070b10' : rng() < 0.5 ? '#1a1912' : '#17160f';
          ctx.fillRect(x, y, px, px);
          continue;
        }
        if (ch === 'u' || ch === 'U') {
          // Underpass: stained concrete, a drain channel down the middle.
          ctx.fillStyle = '#34332f';
          ctx.fillRect(x, y, px, px);
          for (let i = 0; i < 60; i += 1) {
            ctx.fillStyle = rng() < 0.5 ? 'rgba(70,66,58,0.4)' : 'rgba(5,5,5,0.4)';
            ctx.fillRect(x + rng() * px, y + rng() * px, 2 + rng() * 4, 1 + rng() * 3);
          }
          ctx.fillStyle = '#1c1b19';
          const ns = map.isStreet(c, r - 1) || map.isStreet(c, r + 1);
          if (ns) ctx.fillRect(x + px / 2 - 2, y, 4, px);
          else ctx.fillRect(x, y + px / 2 - 2, px, 4);
          continue;
        }
        if (ch === 'g' || ch === 'n') {
          // Lawn: dark grass with lighter blades.
          ctx.fillStyle = '#233120';
          ctx.fillRect(x, y, px, px);
          for (let i = 0; i < 120; i += 1) {
            ctx.fillStyle = rng() < 0.5 ? 'rgba(60,84,48,0.45)' : 'rgba(10,16,8,0.4)';
            ctx.fillRect(x + rng() * px, y + rng() * px, 1, 2 + rng() * 2);
          }
          continue;
        }
        if (ch === 'b') {
          // Bridge deck planks.
          const across = map.isStreet(c - 1, r) || map.isStreet(c + 1, r);
          ctx.fillStyle = '#3a2e24';
          ctx.fillRect(x, y, px, px);
          ctx.fillStyle = 'rgba(0,0,0,0.4)';
          for (let k = 0; k < px; k += 6) {
            if (across) ctx.fillRect(x + k, y, 1, px);
            else ctx.fillRect(x, y + k, px, 1);
          }
          continue;
        }
        if (ARCADE.has(ch)) {
          // Arcade floor tiles.
          for (let ty = 0; ty < px; ty += 16) {
            for (let tx = 0; tx < px; tx += 16) {
              ctx.fillStyle = ((tx + ty) / 16) % 2 === 0 ? '#45423e' : '#3a3734';
              ctx.fillRect(x + tx, y + ty, 16, 16);
            }
          }
          continue;
        }
        if (ch === 'z') {
          // Stone steps.
          const vertical = map.isStreet(c, r - 1) || map.isStreet(c, r + 1);
          for (let k = 0; k < px; k += 8) {
            ctx.fillStyle = (k / 8) % 2 === 0 ? '#4c4a45' : '#2e2d2a';
            if (vertical) ctx.fillRect(x + 10, y + k, px - 20, 8);
            else ctx.fillRect(x + k, y + 10, 8, px - 20);
          }
          continue;
        }
        if (map.isShrine(c, r)) {
          ctx.fillStyle = '#58544a';
          ctx.fillRect(x, y, px, px);
          for (let i = 0; i < 90; i += 1) {
            ctx.fillStyle = rng() < 0.5 ? 'rgba(120,112,95,0.35)' : 'rgba(20,18,14,0.35)';
            ctx.fillRect(x + rng() * px, y + rng() * px, 1.5, 1.5);
          }
          // Stepping stones along the path.
          ctx.fillStyle = '#7d776a';
          const horizontal = !map.isStreet(c, r - 1) && !map.isStreet(c, r + 1);
          for (let k = 0; k < 4; k += 1) {
            const t = (k + 0.5) / 4;
            if (horizontal) ctx.fillRect(x + t * px - 5, y + px / 2 - 7, 10, 14);
            else ctx.fillRect(x + px / 2 - 7, y + t * px - 5, 14, 10);
          }
          continue;
        }
        if (ch === 'A' || ch === 'H') {
          ctx.fillStyle = ch === 'A' ? '#6b655c' : '#3f3c37';
          ctx.fillRect(x, y, px, px);
          ctx.strokeStyle = 'rgba(0,0,0,0.35)';
          ctx.lineWidth = 1;
          for (let k = 0; k <= px; k += 8) {
            ctx.beginPath();
            ctx.moveTo(x + k, y);
            ctx.lineTo(x + k, y + px);
            ctx.moveTo(x, y + k);
            ctx.lineTo(x + px, y + k);
            ctx.stroke();
          }
          continue;
        }

        // Asphalt
        ctx.fillStyle = this.asphalt;
        ctx.fillRect(x, y, px, px);
        for (let i = 0; i < 70; i += 1) {
          const v = rng();
          ctx.fillStyle = v < 0.5 ? 'rgba(90,90,95,0.25)' : 'rgba(10,10,12,0.3)';
          ctx.fillRect(x + rng() * px, y + rng() * px, 1 + rng() * 2, 1 + rng() * 2);
        }
        // Patches of repaired asphalt.
        if (rng() < 0.3) {
          ctx.fillStyle = 'rgba(15,15,17,0.45)';
          ctx.fillRect(x + rng() * px * 0.6, y + rng() * px * 0.6, 10 + rng() * 22, 8 + rng() * 18);
        }
        // Concrete gutters behind the fences.
        ctx.fillStyle = '#45443f';
        if (!map.isStreet(c - 1, r)) ctx.fillRect(x, y, inset - 3, px);
        if (!map.isStreet(c + 1, r)) ctx.fillRect(x + px - inset + 3, y, inset - 3, px);
        if (!map.isStreet(c, r - 1)) ctx.fillRect(x, y, px, inset - 3);
        if (!map.isStreet(c, r + 1)) ctx.fillRect(x, y + px - inset + 3, px, inset - 3);
        // White edge lines.
        ctx.fillStyle = 'rgba(214,214,204,0.6)';
        const lw = 2.2;
        if (!map.isStreet(c - 1, r)) ctx.fillRect(x + inset, y, lw, px);
        if (!map.isStreet(c + 1, r)) ctx.fillRect(x + px - inset - lw, y, lw, px);
        if (!map.isStreet(c, r - 1)) ctx.fillRect(x, y + inset, px, lw);
        if (!map.isStreet(c, r + 1)) ctx.fillRect(x, y + px - inset - lw, px, lw);
        // Manhole
        if (rng() < 0.12) {
          ctx.fillStyle = '#3b3a37';
          ctx.beginPath();
          ctx.arc(x + px * (0.35 + rng() * 0.3), y + px * (0.35 + rng() * 0.3), 5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    // 止まれ (stop) markings before junctions. [cell, rotation in radians]
    // Stop markings on north-south streets just before each junction.
    const stops: [number, number, number][] = [];
    const isJunction = (c: number, r: number) => map.isStreet(c, r) && map.openNeighbours(c, r) >= 3;
    for (let r = 0; r < map.height; r += 1) {
      for (let c = 0; c < map.width; c += 1) {
        if (map.char(c, r) !== '.' || map.isShrine(c, r)) continue;
        const corridor = map.isStreet(c, r - 1) && map.isStreet(c, r + 1) && !map.isStreet(c - 1, r) && !map.isStreet(c + 1, r);
        if (!corridor) continue;
        if (isJunction(c, r - 1)) stops.push([c, r, 0]);
        if (isJunction(c, r + 1)) stops.push([c, r, Math.PI]);
      }
    }
    for (const [c, r, rot] of stops) {
      ctx.save();
      ctx.translate((c + 0.5) * px, (r + 0.5) * px);
      ctx.rotate(rot);
      ctx.fillStyle = 'rgba(225,225,215,0.7)';
      ctx.fillRect(-22, -px / 2 + 3, 44, 3);
      ctx.scale(1, 1.9);
      ctx.font = 'bold 12px "Yu Gothic", "Meiryo", "Hiragino Sans", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('止まれ', 0, -px / 2 / 1.9 + 9);
      ctx.restore();
    }

    if (this.snow) {
      // Snow over everything except under cover; trodden darker down the middle of streets.
      for (let r = 0; r < map.height; r += 1) {
        for (let c = 0; c < map.width; c += 1) {
          const ch = map.char(c, r);
          if (ARCADE.has(ch) || ch === 'u' || ch === 'U' || ch === 'w') continue;
          ctx.fillStyle = 'rgba(214,220,230,0.86)';
          ctx.fillRect(c * px, r * px, px, px);
          for (let i = 0; i < 40; i += 1) {
            ctx.fillStyle = rng() < 0.5 ? 'rgba(255,255,255,0.35)' : 'rgba(150,160,175,0.25)';
            ctx.fillRect(c * px + rng() * px, r * px + rng() * px, 2 + rng() * 3, 1 + rng() * 2);
          }
          if (map.isStreet(c, r)) {
            ctx.fillStyle = 'rgba(120,128,140,0.18)';
            const ns = map.isStreet(c, r - 1) || map.isStreet(c, r + 1);
            if (ns) ctx.fillRect(c * px + px * 0.3, r * px, px * 0.4, px);
            else ctx.fillRect(c * px, r * px + px * 0.3, px, px * 0.4);
          }
        }
      }
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 8;
    // Rain makes the street a dark mirror for the lights.
    const material = new THREE.MeshStandardMaterial({
      map: texture,
      roughness: this.wet ? 0.32 : 0.82,
      metalness: this.wet ? 0.18 : 0.04,
    });
    addSurfaceDetail(material, 'ground', this.wet);
    const geometry = new THREE.PlaneGeometry(map.width * CELL, map.height * CELL);
    const ground = new THREE.Mesh(geometry, material);
    ground.rotation.x = -Math.PI / 2;
    ground.position.set((map.width * CELL) / 2, 0, (map.height * CELL) / 2);
    ground.receiveShadow = true;
    this.group.add(ground);
    this.disposables.push(texture, material, geometry);

    const outerGeometry = new THREE.PlaneGeometry(1200, 1200);
    const outerMaterial = new THREE.MeshStandardMaterial({ color: this.snow ? '#8d939c' : '#0f0e0b', roughness: 1 });
    const outer = new THREE.Mesh(outerGeometry, outerMaterial);
    outer.rotation.x = -Math.PI / 2;
    outer.position.set((map.width * CELL) / 2, -0.02, (map.height * CELL) / 2);
    this.group.add(outer);
    this.disposables.push(outerGeometry, outerMaterial);
  }

  private chebyshevToStreet(c: number, r: number, maxRing: number): number {
    for (let ring = 1; ring <= maxRing; ring += 1) {
      for (let dr = -ring; dr <= ring; dr += 1) {
        for (let dc = -ring; dc <= ring; dc += 1) {
          if (Math.max(Math.abs(dc), Math.abs(dr)) !== ring) continue;
          if (this.map.isStreet(c + dc, r + dr)) return ring;
        }
      }
    }
    return maxRing + 1;
  }

  private isGrove(c: number, r: number): boolean {
    let touchesShrine = false;
    for (let dr = -1; dr <= 1; dr += 1) {
      for (let dc = -1; dc <= 1; dc += 1) {
        if (this.map.isShrine(c + dc, r + dr)) touchesShrine = true;
      }
    }
    if (!touchesShrine) return false;
    for (const s of SIDES) {
      const nc = c + s.dc;
      const nr = r + s.dr;
      if (this.map.isStreet(nc, nr) && !this.map.isShrine(nc, nr)) return false;
    }
    return true;
  }

  private buildBlocks(): void {
    const map = this.map;
    for (let r = -2; r < map.height + 2; r += 1) {
      for (let c = -2; c < map.width + 2; c += 1) {
        if (map.isStreet(c, r)) continue;
        if (this.custom.has(this.key(c, r))) continue;
        const bch = map.char(c, r);
        if (bch === 'C' || bch === 'E' || bch === 'w' || bch === 'r' || bch === 'e') continue;
        if (bch === 't') {
          this.trees(c, r);
          continue;
        }
        if (bch === 'h') {
          this.hedge(c, r);
          continue;
        }
        if (bch === 'j') {
          this.grove(c, r, false);
          continue;
        }
        const ring = this.chebyshevToStreet(c, r, 2);
        if (ring > 2) continue;
        if (ring === 1 && SIDES.some((sd) => ARCADE.has(map.char(c + sd.dc, r + sd.dr)))) {
          this.shop(c, r);
          continue;
        }
        if (this.isGrove(c, r)) {
          this.grove(c, r);
          continue;
        }
        this.house(c, r, ring);
      }
    }
  }

  private house(c: number, r: number, ring: number): void {
    const rng = this.rng;
    const b = this.batch;
    const cx = (c + 0.5) * CELL;
    const cz = (r + 0.5) * CELL;
    const inset = 0.8 + rng() * 0.35;
    const w = CELL - inset * 2;
    const d = CELL - inset * 2 - rng() * 0.5;
    const tall = rng() < (ring === 1 ? 0.16 : 0.3);
    const floors = tall ? 3 : 2;
    const h = floors * 2.75 + 0.35;
    const wallColor = this.pick(this.wallColors);
    const roofColor = this.pick(this.roofColors);

    b.add('wall', this.box, wallColor, cx, h / 2, cz, 0, w, h, d);

    const flat = tall || rng() < 0.12;
    if (flat) {
      b.add('roof', this.box, roofColor, cx, h + 0.12, cz, 0, w + 0.15, 0.24, d + 0.15);
      // Rooftop water tank / AC unit silhouettes.
      if (rng() < 0.5) b.add('metal', this.box, '#6f6e69', cx + (rng() - 0.5) * 3, h + 0.6, cz + (rng() - 0.5) * 3, 0, 1.2, 0.9, 0.9);
    } else {
      const alongX = rng() < 0.5;
      const hr = 1.5 + rng() * 0.7;
      const roofW = (alongX ? d : w) + 1.0;
      const len = (alongX ? w : d) + 0.8;
      b.add('roof', this.prism, roofColor, cx, h + hr / 3, cz, alongX ? Math.PI / 2 : 0, roofW / 1.732, len, hr / 1.5, -Math.PI / 2);
      // Rain gutters under both eaves.
      for (const k of [-1, 1]) {
        const off = k * (roofW / 2 + 0.04);
        if (alongX) b.add('metal', this.box, '#5f5e5a', cx, h - 0.07, cz + off, 0, len, 0.11, 0.13);
        else b.add('metal', this.box, '#5f5e5a', cx + off, h - 0.07, cz, 0, 0.13, 0.11, len);
      }
    }

    // Windows and doors on faces that look onto a street.
    let doorPlaced = false;
    for (const s of SIDES) {
      const facesStreet = this.map.isStreet(c + s.dc, r + s.dr);
      if (!facesStreet && !(ring > 1 && rng() < 0.35)) continue;
      if (facesStreet && this.map.isShrine(c + s.dc, r + s.dr) && rng() < 0.6) continue;
      const half = s.dc !== 0 ? w / 2 : d / 2;
      const span = s.dc !== 0 ? d : w;
      const fx = cx + s.dc * (half + 0.03);
      const fz = cz + s.dr * (half + 0.03);
      for (let f = 0; f < floors; f += 1) {
        for (const k of [-1, 1]) {
          if (f === 0 && ring > 1) continue;
          const off = k * span * 0.26;
          const y = 1.55 + f * 2.75;
          const px = s.dc !== 0 ? fx : fx + off;
          const pz = s.dc !== 0 ? fz + off : fz;
          const lit = rng() < (ring === 1 ? 0.3 : 0.24);
          if (lit) {
            const tone = this.pick(WINDOW_LIT);
            b.add('litWindow', this.plane, tone, px, y, pz, s.rot, 1.15, 0.95, 1);
            this.curtains(px, y, pz, s, tone);
          } else {
            b.add('glass', this.plane, '#141a22', px, y, pz, s.rot, 1.15, 0.95, 1);
          }
          // Sill
          b.add('concrete', this.box, '#5d5b55', px + s.dc * 0.06, y - 0.55, pz + s.dr * 0.06, s.rot, 1.35, 0.08, 0.14);
          this.windowTrim(px, y, pz, s, f);
          // Balcony rail on upper floors, sometimes.
          if (f > 0 && facesStreet && k === 1 && rng() < 0.25) {
            b.add('metal', this.box, '#4d4f52', px + s.dc * 0.55, y - 0.65, pz + s.dr * 0.55, s.rot, span * 0.9, 0.9, 0.06);
            this.balcony(px - s.dc * 0.03, y, pz - s.dr * 0.03, s, span * 0.9, off);
          }
        }
      }
      if (facesStreet && !doorPlaced && ring === 1) {
        doorPlaced = true;
        b.add('wood', this.plane, '#3a2c22', fx + s.dc * 0.01, 1.05, fz + s.dr * 0.01, s.rot, 0.95, 2.1, 1);
        this.doorDetails(fx, fz, s);
        // Small house-number plate
        b.add('concrete', this.box, '#d8d2c2', fx + s.dc * 0.03 + (s.dc === 0 ? 0.8 : 0), 1.6, fz + s.dr * 0.03 + (s.dr === 0 ? 0.8 : 0), s.rot, 0.28, 0.16, 0.03);
        this.doorTrim(fx, fz, s);
      }
      // AC unit on some street faces
      if (facesStreet && rng() < 0.35) {
        const off = (rng() - 0.5) * span * 0.6;
        const px = s.dc !== 0 ? fx + s.dc * 0.2 : fx + off;
        const pz = s.dc !== 0 ? fz + off : fz + s.dr * 0.2;
        this.prop('ac', px + s.dc * 0.08, pz + s.dr * 0.08, s.rot, { y: 0.04 });
        // The refrigerant pipe running up the wall.
        const sideX = s.dc === 0 ? 0.48 : 0;
        const sideZ = s.dc === 0 ? 0 : 0.48;
        b.add('concrete', this.box, '#d6d2c6', px + sideX - s.dc * 0.08, 1.2, pz + sideZ - s.dr * 0.08, s.rot, 0.09, 1.7, 0.09);
      }
    }

    this.houseExtras(cx, cz, w, d, h, floors, flat, c, r);
    if (ring === 1) this.fences(c, r);
  }

  /** Aluminium frame, a mullion, and sometimes a rain-shutter box or a ground-floor grille. */
  private windowTrim(px: number, y: number, pz: number, s: Side, floor: number): void {
    const b = this.batch;
    const rng = this.deco;
    // Frame: four bars just outside the pane's edges, so nothing sits in the pane's plane (no z-fighting).
    const along = (k: number): [number, number] => (s.dc === 0 ? [px + k, pz] : [px, pz + k]);
    for (const k of [-0.61, 0.61]) {
      const [bx, bz] = along(k);
      b.add('metal', this.box, '#3d3f42', bx, y, bz, s.rot, 0.07, 1.09, 0.04);
    }
    for (const dy of [-0.51, 0.51]) b.add('metal', this.box, '#3d3f42', px, y + dy, pz, s.rot, 1.29, 0.07, 0.04);
    b.add('metal', this.box, '#4a4c50', px + s.dc * 0.02, y, pz + s.dr * 0.02, s.rot, 0.04, 0.95, 0.02);
    if (floor > 0 && rng() < 0.45) {
      b.add('metal', this.box, '#8b8a84', px + s.dc * 0.09, y + 0.62, pz + s.dr * 0.09, s.rot, 1.36, 0.2, 0.18);
    } else if (floor === 0 && rng() < 0.5) {
      for (const k of [-0.36, -0.12, 0.12, 0.36]) {
        const gx = s.dc === 0 ? k : 0;
        const gz = s.dc === 0 ? 0 : k;
        b.add('metal', this.box, '#2c2e31', px + gx + s.dc * 0.07, y, pz + gz + s.dr * 0.07, s.rot, 0.03, 0.95, 0.03);
      }
      b.add('metal', this.box, '#2c2e31', px + s.dc * 0.07, y, pz + s.dr * 0.07, s.rot, 1.0, 0.03, 0.03);
    }
  }

  /** Balcony floor slab and a laundry pole; by day, washing hangs on it. */
  private balcony(px: number, y: number, pz: number, s: Side, span: number, off: number): void {
    const b = this.batch;
    const rng = this.deco;
    // The rail spans the facade; centre the slab on it rather than on this window.
    const cx = s.dc === 0 ? px - off : px;
    const cz = s.dc === 0 ? pz : pz - off;
    b.add('concrete', this.box, '#6b6963', cx + s.dc * 0.3, y - 1.12, cz + s.dr * 0.3, s.rot, span, 0.12, 0.62);
    const poleY = y + 0.55;
    const ox = s.dc * 0.38;
    const oz = s.dr * 0.38;
    b.add('metal', this.box, '#9a9a96', cx + ox, poleY, cz + oz, s.rot, span * 0.85, 0.04, 0.04);
    if (!this.daylight) return;
    const items = 3 + Math.floor(rng() * 4);
    for (let i = 0; i < items; i += 1) {
      const along = (rng() - 0.5) * span * 0.75;
      const tall = 0.45 + rng() * 0.45;
      const wide = 0.3 + rng() * 0.35;
      const col = this.pickWith(rng, ['#e8e4da', '#9fb7d4', '#d98b8b', '#e6d27a', '#7f9a7a', '#f0f0ec', '#5a6a8a']);
      const lx = s.dc === 0 ? cx + along : cx + ox;
      const lz = s.dc === 0 ? cz + oz : cz + along;
      b.add('wall', this.box, col, lx, poleY - tall / 2 - 0.03, lz, s.rot, wide, tall, 0.03);
    }
  }

  /** Canopy over the door, an intercom, and the electricity / gas meters. */
  private doorTrim(fx: number, fz: number, s: Side): void {
    const b = this.batch;
    const rng = this.deco;
    b.add('concrete', this.box, '#4b4a47', fx + s.dc * 0.3, 2.32, fz + s.dr * 0.3, s.rot, 1.4, 0.07, 0.6);
    const side = (k: number): [number, number] => (s.dc === 0 ? [fx + k, fz] : [fx, fz + k]);
    const [ix, iz] = side(-0.72);
    b.add('metal', this.box, '#d9d6cc', ix + s.dc * 0.03, 1.35, iz + s.dr * 0.03, s.rot, 0.12, 0.18, 0.05);
    if (rng() < 0.8) {
      const [mx, mz] = side(1.45);
      b.add('metal', this.box, '#c4c1b8', mx + s.dc * 0.06, 1.5, mz + s.dr * 0.06, s.rot, 0.34, 0.46, 0.12);
      b.add('glass', this.box, '#1d232a', mx + s.dc * 0.125, 1.56, mz + s.dr * 0.125, s.rot, 0.2, 0.14, 0.01);
      b.add('metal', this.box, '#b5b2a8', mx + s.dc * 0.08, 0.7, mz + s.dr * 0.08, s.rot, 0.28, 0.36, 0.18);
    }
  }

  /** Plinth, floor bands, a drainpipe, and roof clutter: water tanks, antennas, parapets. */
  private houseExtras(cx: number, cz: number, w: number, d: number, h: number, floors: number, flat: boolean, c: number, r: number): void {
    const b = this.batch;
    const rng = this.deco;
    b.add('concrete', this.box, '#57554f', cx, 0.2, cz, 0, w + 0.02, 0.4, d + 0.02);
    const band = this.pickWith(rng, ['#6a665f', '#5d5a54', '#77736b', '#8a857b']);
    for (let f = 1; f < floors; f += 1) b.add('concrete', this.box, band, cx, f * 2.75 + 0.15, cz, 0, w + 0.1, 0.14, d + 0.1);
    // Drainpipe down a corner on the street side.
    const facing = SIDES.find((sd) => this.map.isStreet(c + sd.dc, r + sd.dr));
    if (facing) {
      const k = rng() < 0.5 ? -1 : 1;
      const px = facing.dc !== 0 ? cx + facing.dc * (w / 2 + 0.08) : cx + k * (w / 2 - 0.15);
      const pz = facing.dc !== 0 ? cz + k * (d / 2 - 0.15) : cz + facing.dr * (d / 2 + 0.08);
      b.add('metal', this.cyl, '#7c7a74', px, h / 2, pz, 0, 0.1, h, 0.1);
    }
    if (flat) {
      // Low parapet around the roof.
      b.add('concrete', this.box, '#6c6a64', cx, h + 0.42, cz - d / 2, 0, w + 0.15, 0.36, 0.12);
      b.add('concrete', this.box, '#6c6a64', cx, h + 0.42, cz + d / 2, 0, w + 0.15, 0.36, 0.12);
      b.add('concrete', this.box, '#6c6a64', cx - w / 2, h + 0.42, cz, 0, 0.12, 0.36, d + 0.15);
      b.add('concrete', this.box, '#6c6a64', cx + w / 2, h + 0.42, cz, 0, 0.12, 0.36, d + 0.15);
      if (rng() < 0.45) {
        // Water tank on a steel stand.
        const tx = cx + (rng() - 0.5) * (w - 2.5);
        const tz = cz + (rng() - 0.5) * (d - 2.5);
        for (const [ax, az] of [[-0.45, -0.45], [0.45, -0.45], [-0.45, 0.45], [0.45, 0.45]]) {
          b.add('metal', this.box, '#3f4144', tx + ax, h + 0.75, tz + az, 0, 0.08, 1.1, 0.08);
        }
        b.add('metal', this.cyl, this.pickWith(rng, ['#9aa3a8', '#b8b3a4', '#7f8f99']), tx, h + 1.85, tz, 0, 1.4, 1.3, 1.4);
      }
    } else if (rng() < 0.4) {
      // TV antenna on the ridge (the gable roof's apex is about 1.5-2.2 m up).
      const ax = cx + (rng() - 0.5) * 2;
      const az = cz + (rng() - 0.5) * 2;
      b.add('metal', this.cyl, '#55585c', ax, h + 2.6, az, 0, 0.05, 2.4, 0.05);
      const rot = rng() * Math.PI;
      for (const y of [3.3, 3.6, 3.85]) b.add('metal', this.box, '#55585c', ax, h + y, az, rot, 1.3 - (y - 3.3), 0.03, 0.03);
    }
  }

  private pickWith<T>(rng: Rng, list: readonly T[]): T {
    return list[Math.floor(rng() * list.length)];
  }

  /** Block walls / hedges along every edge of this lot that faces a street. */
  private fences(c: number, r: number): void {
    const map = this.map;
    const rng = this.rng;
    const style = rng();
    let gateMade = false;
    for (const s of SIDES) {
      const nc = c + s.dc;
      const nr = r + s.dr;
      if (!map.isStreet(nc, nr)) continue;
      const nch = map.char(nc, nr);
      if (nch === 'A' || nch === 'H') continue;
      const shrineSide = map.isShrine(nc, nr);

      let x0: number, x1: number, z0: number, z1: number;
      if (s.dc !== 0) {
        const x = s.dc > 0 ? (c + 1) * CELL + FENCE_INSET : c * CELL - FENCE_INSET;
        x0 = x1 = x;
        z0 = r * CELL - (map.isStreet(c, r - 1) ? FENCE_INSET : 0);
        z1 = (r + 1) * CELL + (map.isStreet(c, r + 1) ? FENCE_INSET : 0);
      } else {
        const z = s.dr > 0 ? (r + 1) * CELL + FENCE_INSET : r * CELL - FENCE_INSET;
        z0 = z1 = z;
        x0 = c * CELL - (map.isStreet(c - 1, r) ? FENCE_INSET : 0);
        x1 = (c + 1) * CELL + (map.isStreet(c + 1, r) ? FENCE_INSET : 0);
      }

      const kind = shrineSide ? 'stone' : style < 0.62 ? 'block' : style < 0.85 ? 'hedge' : 'rail';
      const wantGate = !gateMade && !shrineSide && kind !== 'hedge';
      if (wantGate) gateMade = true;
      const segments: [number, number][] = [];
      const length = s.dc !== 0 ? z1 - z0 : x1 - x0;
      if (wantGate) {
        const g0 = length * 0.5 - 0.75;
        segments.push([0, g0], [g0 + 1.5, length]);
      } else {
        segments.push([0, length]);
      }
      for (const [a, bnd] of segments) {
        const segLen = bnd - a;
        const mid = a + segLen / 2;
        const px = s.dc !== 0 ? x0 : x0 + mid;
        const pz = s.dc !== 0 ? z0 + mid : z0;
        this.fenceSegment(kind, px, pz, segLen, s);
      }
      if (wantGate) {
        const mid = length * 0.5;
        for (const k of [-0.82, 0.82]) {
          const px = s.dc !== 0 ? x0 : x0 + mid + k;
          const pz = s.dc !== 0 ? z0 + mid + k : z0;
          this.batch.add('concrete', this.box, '#6d6a63', px, 0.8, pz, 0, 0.32, 1.6, 0.32);
        }
        this.gateExtras(x0, z0, mid, s);
        // Potted plant by the gate
        if (rng() < 0.6) {
          const inward = 0.6;
          const px = s.dc !== 0 ? x0 - s.dc * inward : x0 + mid + 1.3;
          const pz = s.dc !== 0 ? z0 + mid + 1.3 : z0 - s.dr * inward;
          const crng = this.clutter;
          this.prop(crng() < 0.6 ? 'plant' : 'plantSmall', px, pz);
          if (crng() < 0.5) this.prop(crng() < 0.5 ? 'pots' : 'plantSmall', px + (s.dc !== 0 ? 0 : 0.6), pz + (s.dc !== 0 ? 0.6 : 0));
        }
      }
    }
  }

  /** Mailbox and nameplate on a gate post, sometimes a bicycle parked inside. */
  private gateExtras(x0: number, z0: number, mid: number, s: Side): void {
    const b = this.batch;
    const rng = this.deco;
    const post = (k: number, inward: number): [number, number] =>
      s.dc !== 0 ? [x0 - s.dc * inward, z0 + mid + k] : [x0 + mid + k, z0 - s.dr * inward];
    const [mx, mz] = post(-0.82, -0.2);
    b.add('metal', this.box, this.pickWith(rng, ['#b8b8b2', '#3d4043', '#8a2b26', '#c9c4b3']), mx, 1.2, mz, s.rot, 0.36, 0.3, 0.16);
    const [nx, nz] = post(0.82, -0.18);
    b.add('concrete', this.box, '#e2dccb', nx, 1.3, nz, s.rot, 0.2, 0.3, 0.03);
    if (rng() < 0.3) {
      // A bicycle (two wheels and a frame), parked just inside the gate.
      const [bx, bz] = post(2.1, 0.9);
      const along = s.dc !== 0 ? 0 : Math.PI / 2; // wheels line up with the fence
      const fx = s.dc !== 0 ? 0 : 0.5;
      const fz = s.dc !== 0 ? 0.5 : 0;
      const col = this.pickWith(rng, ['#9b2d2a', '#2f4b6e', '#c9c4b8', '#2d2f31', '#3f6b4a']);
      for (const k of [-1, 1]) {
        b.add('metal', this.cyl, '#1c1d1f', bx + fx * k, 0.34, bz + fz * k, along, 0.66, 0.04, 0.66, 0, Math.PI / 2);
      }
      b.add('metal', this.box, col, bx, 0.6, bz, s.dc !== 0 ? Math.PI / 2 : 0, 0.95, 0.06, 0.05);
      b.add('metal', this.box, col, bx + fx * 0.15, 0.5, bz + fz * 0.15, s.dc !== 0 ? Math.PI / 2 : 0, 0.05, 0.4, 0.05);
      b.add('metal', this.box, '#1c1d1f', bx - fx * 0.2, 0.86, bz - fz * 0.2, 0, 0.22, 0.05, 0.22);
      b.add('metal', this.box, '#7c7f83', bx + fx * 0.8, 0.95, bz + fz * 0.8, s.dc !== 0 ? 0 : Math.PI / 2, 0.55, 0.04, 0.04);
      // Front basket, the classic mamachari.
      b.add('metal', this.box, '#8a8d90', bx + fx * 1.05, 0.78, bz + fz * 1.05, 0, 0.3, 0.22, 0.3);
    }
  }

  private fenceSegment(kind: string, x: number, z: number, len: number, s: Side): void {
    const b = this.batch;
    const alongZ = s.dc !== 0;
    const inwardX = -s.dc;
    const inwardZ = -s.dr;
    if (kind === 'block') {
      const color = this.pick(BLOCK_WALL);
      const t = 0.16;
      b.add('concrete', this.box, color, x + inwardX * t / 2, 0.68, z + inwardZ * t / 2, 0, alongZ ? t : len, 1.36, alongZ ? len : t);
      b.add('concrete', this.box, '#5e5c57', x + inwardX * t / 2, 1.39, z + inwardZ * t / 2, 0, alongZ ? t + 0.06 : len, 0.06, alongZ ? len : t + 0.06);
    } else if (kind === 'hedge') {
      const t = 0.6;
      b.add('foliage', this.box, '#1b2717', x + inwardX * t / 2, 0.75, z + inwardZ * t / 2, 0, alongZ ? t : len, 1.5, alongZ ? len : t);
    } else if (kind === 'rail') {
      const t = 0.18;
      b.add('concrete', this.box, '#77746c', x + inwardX * t / 2, 0.3, z + inwardZ * t / 2, 0, alongZ ? t : len, 0.6, alongZ ? len : t);
      b.add('metal', this.box, '#2f3134', x + inwardX * t / 2, 1.0, z + inwardZ * t / 2, 0, alongZ ? 0.04 : len, 0.8, alongZ ? len : 0.04);
    } else {
      // stone kerb beside the shrine path
      const t = 0.5;
      b.add('concrete', this.box, '#4e5047', x + inwardX * t / 2, 0.32, z + inwardZ * t / 2, 0, alongZ ? t : len, 0.64, alongZ ? len : t);
    }
  }

  /**
   * A bamboo grove (beside a shrine, or a 'j' cell): tall, thin, slightly leaning
   * culms with flat sprays of narrow leaves up top. Cells deep inside a grove,
   * which nobody sees up close, get fewer culms.
   */
  private grove(c: number, r: number, fenced = true): void {
    const rng = this.rng;
    const b = this.batch;
    const inner = this.chebyshevToStreet(c, r, 1) > 1;
    const lean = this.groveLean;
    const count = lean ? 8 + Math.floor(rng() * 4) : inner ? 9 + Math.floor(rng() * 4) : 16 + Math.floor(rng() * 7);
    for (let i = 0; i < count; i += 1) {
      const bx = c * CELL + 0.6 + rng() * (CELL - 1.2);
      const bz = r * CELL + 0.6 + rng() * (CELL - 1.2);
      const h = 8 + rng() * 5;
      const d = 0.13 + rng() * 0.07;
      // Small lean; for small angles the culm's axis is roughly (-leanZ, 1, leanX).
      const leanX = (rng() - 0.5) * 0.16;
      const leanZ = (rng() - 0.5) * 0.16;
      const at = (t: number, ox = 0, oz = 0): [number, number, number] => [bx - leanZ * t + ox, t, bz + leanX * t + oz];
      const old = rng() < 0.2;
      const stem = old ? this.pick(['#8a8a4e', '#7f8048']) : this.pick(['#5f7d3c', '#6c8a44', '#557236', '#64823e']);
      const [cx, cy, cz] = at(h / 2);
      b.add('wood', this.culm, stem, cx, cy, cz, 0, d, h, d, leanX, leanZ);
      // Pale nodes ring the culm every metre or so at eye level.
      for (let t = 0.9 + rng() * 0.5; t < 4.5; t += 1.1 + rng() * 0.4) {
        const [nx, ny, nz] = at(t);
        b.add('wood', this.culm, '#a7a36a', nx, ny, nz, 0, d * 1.18, 0.06, d * 1.18, leanX, leanZ);
      }
      // Feathery sprays of narrow leaves along the top half: twigs leave the culm and droop outward.
      const sprays = lean ? 7 + Math.floor(rng() * 3) : 10 + Math.floor(rng() * 6);
      for (let k = 0; k < sprays; k += 1) {
        const t = h * (0.48 + (k / sprays) * 0.54);
        const ang = rng() * Math.PI * 2;
        const reach = 0.05 + rng() * 0.25;
        const [lx, ly, lz] = at(t, Math.cos(ang) * reach, Math.sin(ang) * reach);
        const shade = this.pick(['#4b6c2c', '#587b33', '#43632a', '#62843a', '#3d5a26', '#6f8f3f']);
        const size = 1.0 + rng() * 0.7;
        // Tilt the twig up or down a little; the leaves hang off it either way.
        b.add('leaf', this.spray, shade, lx, ly, lz, -ang, size, size, size, 0, (rng() - 0.4) * 0.6);
      }
    }
    // A few young shoots coming up between them.
    for (let i = 0; i < 4; i += 1) {
      const x = c * CELL + 1 + rng() * (CELL - 2);
      const z = r * CELL + 1 + rng() * (CELL - 2);
      b.add('wood', this.culm, '#6c8a44', x, 0.5, z, 0, 0.12, 1 + rng() * 0.8, 0.12);
    }
    // Fallen leaves, dry and pale, carpet the ground under the culms.
    for (let i = 0; i < (lean ? 14 : 26); i += 1) {
      const x = c * CELL + rng() * CELL;
      const z = r * CELL + rng() * CELL;
      b.add('leaf', this.leafQuad, this.pick(['#7a7348', '#6b6440', '#8a8156', '#5c5a38']), x, 0.02, z, rng() * Math.PI, 0.06, 0.32, 1, -Math.PI / 2);
    }
    if (fenced) this.fences(c, r);
  }

  // ---------------------------------------------------------------- clutter and small details

  private prop(kind: PropKind, x: number, z: number, rotY = this.clutter() * Math.PI * 2, opts: { y?: number; scale?: number; tint?: string } = {}): void {
    this.props.push({ kind, x, y: opts.y ?? 0, z, rotY, scale: opts.scale, tint: opts.tint });
  }

  /** Curtains (and sometimes a half-drawn blind) inside a lit window: a little life behind the glass. */
  private curtains(px: number, y: number, pz: number, s: Side, tone: string): void {
    const rng = this.clutter;
    const style = rng();
    if (style < 0.25) return;
    const b = this.batch;
    const shade = new THREE.Color(tone).multiplyScalar(0.45 + rng() * 0.2);
    // Just in front of the pane, so nothing shares its plane.
    const ox = s.dc * 0.006;
    const oz = s.dr * 0.006;
    const along = (k: number): [number, number] => (s.dc === 0 ? [px + k + ox, pz + oz] : [px + ox, pz + k + oz]);
    if (style < 0.75) {
      const w = 0.22 + rng() * 0.12;
      for (const k of [-1, 1]) {
        const [cx, cz] = along(k * (0.575 - w / 2));
        b.add('litWindow', this.plane, shade, cx, y, cz, s.rot, w, 0.95, 1);
      }
    } else {
      const drop = 0.3 + rng() * 0.45;
      const [cx, cz] = along(0);
      b.add('litWindow', this.plane, shade, cx, y + 0.475 - drop / 2, cz, s.rot, 1.15, drop, 1);
    }
  }

  /** By the front door: a porch lamp, a doormat, an umbrella, and the gas cylinders by the meters. */
  private doorDetails(fx: number, fz: number, s: Side): void {
    const b = this.batch;
    const rng = this.clutter;
    const side = (k: number, out: number): [number, number] => (s.dc === 0 ? [fx + k, fz + s.dr * out] : [fx + s.dc * out, fz + k]);
    if (!this.daylight && rng() < 0.55) {
      const [lx, lz] = side(0.66, 0.07);
      b.add('metal', this.box, '#2e2f31', lx, 2.0, lz, s.rot, 0.12, 0.2, 0.1);
      b.add('glow', this.box, this.pickWith(rng, ['#7a5a34', '#806a48', '#6e5a40']), lx + s.dc * 0.03, 1.96, lz + s.dr * 0.03, s.rot, 0.09, 0.13, 0.06);
    }
    const [mx, mz] = side(0, 0.35);
    b.add('concrete', this.box, this.pickWith(rng, ['#3b3530', '#2f3a33', '#45362c']), mx, 0.01, mz, s.rot, 0.8, 0.02, 0.45);
    if (rng() < 0.3) this.prop('umbrella', ...side(-0.62, 0.18), rng() * 6, { scale: 0.9 });
    if (rng() < 0.45) {
      // LP gas: one or two grey cylinders against the wall.
      const n = rng() < 0.6 ? 2 : 1;
      for (let i = 0; i < n; i += 1) {
        const [gx, gz] = side(-1.5 - i * 0.42, 0.3);
        this.prop('propane', gx, gz, rng() * 6);
      }
    } else if (rng() < 0.5) {
      const [px, pz] = side(-1.4, 0.35);
      this.prop(rng() < 0.5 ? 'plant' : 'pots', px, pz);
    }
  }

  /** Blue and green recycling bins (cans, bottles) beside a row of vending machines. */
  private recyclingBins(x0: number, z0: number, x1: number, z1: number, rot: number): void {
    this.prop('trashCan', x0, z0, rot, { tint: '#2a5fae', scale: 0.9 });
    this.prop('trashCan', x1, z1, rot, { tint: '#2f7a46', scale: 0.9 });
    if (this.clutter() < 0.5) this.prop('bottles', x1 + (x1 - x0) * 0.9, z1 + (z1 - z0) * 0.9);
  }

  /**
   * The little things on a residential street: concrete gutter covers along
   * the walls, manholes, blown litter and leaves, the neighbourhood's rubbish
   * collection points (with crows), boxes outside shops.
   */
  private buildClutter(): void {
    const map = this.map;
    const rng = this.clutter;
    const b = this.batch;
    const lotEdge = (c: number, r: number) => {
      const ch = map.char(c, r);
      return !map.isStreet(c, r) && !'wrtjhKeE'.includes(ch);
    };
    const nearGreen = (c: number, r: number) => {
      for (let dr = -1; dr <= 1; dr += 1) {
        for (let dc = -1; dc <= 1; dc += 1) if ('tjgnh'.includes(map.char(c + dc, r + dr)) || this.isGrove(c + dc, r + dr)) return true;
      }
      return false;
    };
    let lastBins = -99;
    for (let r = 0; r < map.height; r += 1) {
      for (let c = 0; c < map.width; c += 1) {
        if (!map.isStreet(c, r)) continue;
        const ch = map.char(c, r);
        const cx = (c + 0.5) * CELL;
        const cz = (r + 0.5) * CELL;
        // A point d metres in from side s's edge of this cell, u along it (0..1).
        const at = (sd: Side, d: number, u: number): [number, number] => {
          const ex = cx + sd.dc * (CELL / 2 - d);
          const ez = cz + sd.dr * (CELL / 2 - d);
          return sd.dc !== 0 ? [ex, ez + (u - 0.5) * CELL] : [ex + (u - 0.5) * CELL, ez];
        };
        if (ARCADE.has(ch)) {
          // Stacked boxes and crates left out in front of the shops.
          if (rng() < 0.3) {
            const sd = SIDES.find((q) => !map.isStreet(c + q.dc, r + q.dr));
            if (sd) {
              const [x, z] = at(sd, 0.55, 0.2 + rng() * 0.6);
              this.prop(rng() < 0.6 ? 'boxes' : 'crate', x, z);
              if (rng() < 0.5) this.prop('boxes', x + 0.4, z + 0.3, rng() * 6, { y: 0, scale: 0.8 });
            }
          }
          continue;
        }
        if (ch === 'g' || ch === 'n' || ch === 'p' || ch === 'G' || map.isShrine(c, r)) {
          // Fallen leaves on the park paths and lawns.
          this.scatterLeaves(c, r, ch === 'g' || ch === 'n' ? 14 : 22);
          continue;
        }
        if (!'.LFBTSPVAX'.includes(ch)) continue;
        const walls = SIDES.filter((q) => lotEdge(c + q.dc, r + q.dr));

        // Concrete gutter covers between the fence and the white line: slabs, every few a steel grating.
        for (const sd of walls) {
          const slabs = 12;
          for (let i = 0; i < slabs; i += 1) {
            const u = (i + 0.5) / slabs;
            // Stop short of a corner where the street turns.
            if ((u < 0.2 && !lotEdge(c + sd.dc + (sd.dc === 0 ? -1 : 0), r + sd.dr + (sd.dr === 0 ? -1 : 0))) || (u > 0.8 && !lotEdge(c + sd.dc + (sd.dc === 0 ? 1 : 0), r + sd.dr + (sd.dr === 0 ? 1 : 0)))) continue;
            const [x, z] = at(sd, 1.47, u);
            const grate = i % 4 === 1;
            const len = CELL / slabs - 0.035;
            const w = 0.3;
            const sx = sd.dc !== 0 ? w : len;
            const sz = sd.dc !== 0 ? len : w;
            b.add(grate ? 'metal' : 'concrete', this.box, grate ? '#2b2c2d' : this.pickWith(rng, ['#5b5a55', '#55544f', '#605e58']), x, 0.012, z, 0, sx, 0.024, sz);
            if (grate) {
              for (let k = -2; k <= 2; k += 1) {
                const gx = sd.dc !== 0 ? x : x + k * len * 0.18;
                const gz = sd.dc !== 0 ? z + k * len * 0.18 : z;
                b.add('metal', this.box, '#121313', gx, 0.026, gz, 0, sd.dc !== 0 ? w * 0.8 : 0.03, 0.006, sd.dc !== 0 ? 0.03 : w * 0.8);
              }
            }
          }
        }

        // Manhole: a cast-iron lid with a raised ring.
        if (rng() < 0.14) {
          const mx = cx + (rng() - 0.5) * 2.4;
          const mz = cz + (rng() - 0.5) * 2.4;
          b.add('metal', this.cyl, '#34332f', mx, 0.008, mz, 0, 0.7, 0.016, 0.7);
          b.add('metal', this.cyl, '#26251f', mx, 0.012, mz, 0, 0.5, 0.016, 0.5);
          b.add('metal', this.cyl, '#3c3a35', mx, 0.016, mz, 0, 0.18, 0.012, 0.18);
        }

        // Blown litter: a sheet of paper or a flyer.
        if (rng() < 0.3) {
          this.prop('papers', cx + (rng() - 0.5) * 4, cz + (rng() - 0.5) * 4, undefined, { y: 0.01, scale: 0.6 + rng() * 0.4 });
        }

        if (nearGreen(c, r)) this.scatterLeaves(c, r, 10);

        // Rubbish collection point against a wall: bags (sometimes in a folding crow cage), a sign, and crows.
        if (walls.length > 0 && ch !== 'X' && ch !== 'V' && r - lastBins > 2 && rng() < 0.16) {
          lastBins = r;
          const sd = walls[Math.floor(rng() * walls.length)];
          const u = 0.3 + rng() * 0.4;
          const [x, z] = at(sd, 1.75, u);
          const alongX = sd.dc === 0;
          const n = 3 + Math.floor(rng() * 3);
          for (let i = 0; i < n; i += 1) {
            const k = (i / (n - 1) - 0.5) * 1.3 + (rng() - 0.5) * 0.2;
            const o = (rng() - 0.5) * 0.25;
            // Mostly white bags, the odd yellow one (a different ward's) or a blue recycling bag.
            const v = rng();
            const tint = v < 0.7 ? undefined : v < 0.85 ? '#d8cf8e' : '#9fb4cc';
            this.prop(rng() < 0.4 ? 'trashBags' : 'trashBag', x + (alongX ? k : o), z + (alongX ? o : k), undefined, { scale: 0.85 + rng() * 0.3, tint });
          }
          if (rng() < 0.5) {
            // Folding green wire cage over the bags.
            const hw = alongX ? 1.0 : 0.5;
            const hd = alongX ? 0.5 : 1.0;
            const g = '#2f6046';
            for (const [ex, ez] of [[-hw, -hd], [hw, -hd], [-hw, hd], [hw, hd]]) b.add('metal', this.box, g, x + ex, 0.45, z + ez, 0, 0.03, 0.9, 0.03);
            for (const ey of [0.02, 0.9]) {
              for (const ez of [-hd, hd]) b.add('metal', this.box, g, x, ey, z + ez, 0, hw * 2, 0.03, 0.03);
              for (const ex of [-hw, hw]) b.add('metal', this.box, g, x + ex, ey, z, 0, 0.03, 0.03, hd * 2);
            }
            for (let k = -0.6; k <= 0.61; k += 0.3) {
              b.add('metal', this.box, g, x + (alongX ? k * hw : 0), 0.9, z + (alongX ? 0 : k * hd), 0, alongX ? 0.015 : hw * 2, 0.015, alongX ? hd * 2 : 0.015);
            }
          } else {
            // Blue mesh net thrown over them.
            b.add('foliage', this.sphere, '#1f3f6e', x, 0.22, z, 0, alongX ? 1.9 : 0.9, 0.5, alongX ? 0.9 : 1.9);
          }
          // Collection-day sign on a short post by the fence.
          const [sx, sz] = at(sd, 1.36, u + 0.13);
          b.add('metal', this.box, '#8a8b8c', sx, 0.6, sz, 0, 0.04, 1.2, 0.04);
          b.add('concrete', this.box, '#e8e6dc', sx, 1.12, sz, sd.rot, 0.42, 0.3, 0.02);
          b.add('concrete', this.box, '#2f7a4a', sx, 1.24, sz, sd.rot, 0.42, 0.07, 0.025);
          this.map.addBlocker(x, z, 0.75);
          // A crow or two picking at the bags.
          const crows = rng() < 0.6 ? 1 + Math.floor(rng() * 2) : 0;
          for (let i = 0; i < crows; i += 1) {
            const [bx, bz] = at(sd, 2.6 + rng() * 0.6, u + (rng() - 0.5) * 0.3);
            this.perches.push({ x: bx, y: 0, z: bz, rot: rng() * Math.PI * 2, ground: true });
          }
        }
      }
    }
    // Crows on some poles' crossarms.
    for (const pole of this.poles) {
      if (rng() > 0.18) continue;
      const k = rng() < 0.5 ? -0.7 : 0.7;
      this.perches.push({ x: pole.x + (pole.alongX ? k : 0), y: 8.36, z: pole.z + (pole.alongX ? 0 : k), rot: rng() * Math.PI * 2, ground: false });
    }
  }

  /** Dry leaves on the ground of one cell. */
  private scatterLeaves(c: number, r: number, count: number): void {
    const rng = this.clutter;
    for (let i = 0; i < count; i += 1) {
      const x = c * CELL + rng() * CELL;
      const z = r * CELL + rng() * CELL;
      const col = this.pickWith(rng, ['#6b5a34', '#7a6238', '#5a4a2c', '#86683a', '#4f4a2a', '#8a5a2e']);
      this.batch.add('leaf', this.leafQuad, col, x, 0.015, z, rng() * Math.PI, 0.1, 0.16, 1, -Math.PI / 2);
    }
  }

  // ---------------------------------------------------------------- street furniture

  private lampSide(c: number, r: number): { px: number; pz: number; dx: number; dz: number } {
    const map = this.map;
    const cx = (c + 0.5) * CELL;
    const cz = (r + 0.5) * CELL;
    const edge = CELL / 2 - 1.45;
    const order: [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    for (const [dc, dr] of order) {
      if (!map.isStreet(c + dc, r + dr)) {
        return { px: cx + dc * edge, pz: cz + dr * edge, dx: -dc, dz: -dr };
      }
    }
    const diagonals: [number, number][] = [[1, -1], [-1, -1], [1, 1], [-1, 1]];
    for (const [dc, dr] of diagonals) {
      if (!map.isStreet(c + dc, r + dr)) {
        return { px: cx + dc * edge, pz: cz + dr * edge, dx: -dc * 0.707, dz: -dr * 0.707 };
      }
    }
    return { px: cx + edge, pz: cz, dx: -1, dz: 0 };
  }

  private utilityPole(x: number, z: number, crossAlongX: boolean): void {
    const b = this.batch;
    b.add('concrete', this.cyl, '#77766f', x, 4.6, z, 0, 0.32, 9.2, 0.32);
    b.add('metal', this.box, '#3d3e3f', x, 8.3, z, 0, crossAlongX ? 1.8 : 0.1, 0.1, crossAlongX ? 0.1 : 1.8);
    if (this.rng() < 0.3) b.add('metal', this.cyl, '#5d6066', x + 0.35, 6.6, z + 0.35, 0, 0.55, 0.9, 0.55);
    // Blue address plate, step bolts up the pole, and the odd advert sign.
    const rng = this.deco;
    b.add('metal', this.box, '#2c4f86', x, 2.4, z + 0.17, 0, 0.24, 0.62, 0.02);
    b.add('metal', this.box, '#e9e6dc', x, 2.48, z + 0.19, 0, 0.18, 0.36, 0.02);
    for (let y = 2.9; y < 7.6; y += 0.45) b.add('metal', this.box, '#4a4b4c', x, y, z, (y * 3.7) % Math.PI, 0.5, 0.025, 0.025);
    if (rng() < 0.35) b.add('metal', this.box, this.pickWith(rng, ['#d4c27a', '#c9d2dc', '#e2a36f']), x - 0.17, 3.2, z, 0, 0.02, 0.9, 0.3);
    // Yellow-black guard sleeve at the base.
    b.add('concrete', this.cyl, '#b59a2c', x, 0.9, z, 0, 0.36, 0.5, 0.36);
    b.add('concrete', this.cyl, '#1b1b1b', x, 1.25, z, 0, 0.36, 0.2, 0.36);
    this.poles.push({ x, z, alongX: crossAlongX });
  }

  private streetLight(c: number, r: number, id: string, state: LightState, color: string, intensity = 26): void {
    if (this.daylight) state = 'off';
    const side = this.lampSide(c, r);
    const crossAlongX = Math.abs(side.dx) > Math.abs(side.dz);
    this.utilityPole(side.px, side.pz, crossAlongX);
    const arm = 1.5;
    const lx = side.px + side.dx * arm;
    const lz = side.pz + side.dz * arm;
    const ly = 5.5;
    this.batch.add('metal', this.box, '#4a4b4d', (side.px + lx) / 2, ly + 0.25, (side.pz + lz) / 2, Math.atan2(side.dx, side.dz), 0.08, 0.08, arm);
    const headMat = this.glowMaterial(color, 3.2);
    const head = new THREE.Mesh(this.box, headMat);
    head.scale.set(0.55, 0.12, 0.3);
    head.position.set(lx, ly + 0.15, lz);
    head.rotation.y = Math.atan2(side.dx, side.dz);
    this.group.add(head);
    const housing = new THREE.Mesh(this.box, new THREE.MeshStandardMaterial({ color: '#3a3b3d', roughness: 0.6 }));
    this.disposables.push(housing.material as THREE.Material);
    housing.scale.set(0.62, 0.12, 0.36);
    housing.position.set(lx, ly + 0.27, lz);
    housing.rotation.y = head.rotation.y;
    this.group.add(housing);

    const source = this.addLight({
      id,
      lamp: new THREE.Vector3(lx, ly, lz),
      ground: new THREE.Vector2(lx + side.dx * 0.6, lz + side.dz * 0.6),
      radius: 8.5,
      color,
      intensity,
      state,
      cone: true,
    });
    source.addGlow(headMat);
  }

  private vending(c: number, r: number, id: string): void {
    const map = this.map;
    // Stand against whichever side of the street is a wall.
    const sides: [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    const [sdc, sdr] = sides.find(([dc, dr]) => !map.isStreet(c + dc, r + dr)) ?? [1, 0];
    if (sdr !== 0) {
      this.vendingNS(c, r, id, sdr);
      return;
    }
    const east = sdc > 0;
    const dir = east ? 1 : -1;
    const frontX = (c + 0.5) * CELL + dir * (CELL / 2 - FENCE_INSET - 0.04);
    const cz = (r + 0.5) * CELL;
    const rot = east ? -Math.PI / 2 : Math.PI / 2;
    const frontTex = createVendingTexture();
    const glow = new THREE.MeshBasicMaterial({ map: frontTex });
    glow.color.setScalar(1.15);
    this.disposables.push(glow, frontTex);
    const bodies = ['#b8211d', '#e8e6e0', '#1f4fa8'];
    for (let i = 0; i < 2; i += 1) {
      const z = cz + (i === 0 ? -0.56 : 0.56);
      this.batch.add('metal', this.box, bodies[(c + r + i) % bodies.length], frontX + dir * 0.4, 0.92, z, 0, 0.8, 1.84, 1.05);
      const panel = new THREE.Mesh(this.plane, glow);
      panel.scale.set(0.86, 1.25, 1);
      panel.position.set(frontX - dir * 0.012, 1.12, z);
      panel.rotation.y = rot;
      this.group.add(panel);
    }
    const source = this.addLight({
      id,
      lamp: new THREE.Vector3(frontX - dir * 0.9, 1.3, cz),
      ground: new THREE.Vector2(frontX - dir * 1.8, cz),
      radius: 4.6,
      color: '#dcecff',
      intensity: 7,
      exposure: 0.75,
    });
    source.addGlow(glow);
    this.recyclingBins(frontX - dir * 0.3, cz + 1.45, frontX - dir * 0.3, cz + 2.1, rot);
    this.vendingSpots.push(new THREE.Vector3(frontX, 1, cz));
    this.blockers.push({ x: frontX - dir * 0.1, z: cz, r: 0.15 });
  }

  private buildStreetFurniture(): void {
    const map = this.map;
    for (let r = 0; r < map.height; r += 1) {
      for (let c = 0; c < map.width; c += 1) {
        const ch = map.char(c, r);
        switch (ch) {
          case 'L':
          case 'S':
            this.streetLight(c, r, `L-${c}-${r}`, 'on', (c + r) % 3 === 0 ? '#ffe2b0' : '#d9eaff');
            break;
          case 'F':
            this.streetLight(c, r, `F-${c}-${r}`, 'on', '#d9eaff');
            break;
          case 'B':
            this.streetLight(c, r, 'B-home', 'broken', '#d9eaff', 18);
            break;
          case 'V':
            this.vending(c, r, `V-${c}-${r}`);
            break;
          case 'T':
            this.sensorLight(c, r);
            break;
          default:
            break;
        }
      }
    }

    // Plain utility poles along straight runs so wires read as continuous.
    for (let r = 0; r < map.height; r += 1) {
      for (let c = 0; c < map.width; c += 1) {
        if (map.char(c, r) !== '.' || map.isShrine(c, r)) continue;
        const ns = map.isStreet(c, r - 1) && map.isStreet(c, r + 1) && !map.isStreet(c - 1, r) && !map.isStreet(c + 1, r);
        const ew = map.isStreet(c - 1, r) && map.isStreet(c + 1, r) && !map.isStreet(c, r - 1) && !map.isStreet(c, r + 1);
        if (!ns && !ew) continue;
        if ((c + r) % 2 !== 0) continue;
        const side = this.lampSide(c, r);
        this.utilityPole(side.px, side.pz, ns);
      }
    }

    // Convex traffic mirrors at a few junction corners.
    // Convex traffic mirrors on a blocked corner of each junction.
    const mirrors: [number, number, number, number][] = [];
    for (let r = 0; r < map.height; r += 1) {
      for (let c = 0; c < map.width; c += 1) {
        if (!map.isPassable(c, r) || map.isShrine(c, r) || map.openNeighbours(c, r) < 3) continue;
        for (const [dc, dr] of [[1, 1], [-1, 1], [1, -1], [-1, -1]]) {
          if (!map.isStreet(c + dc, r + dr) && map.isStreet(c + dc, r) && map.isStreet(c, r + dr)) {
            mirrors.push([c, r, dc, dr]);
            break;
          }
        }
      }
    }
    for (const [c, r, dc, dr] of mirrors) {
      const cx = (c + 0.5) * CELL + dc * (CELL / 2 - 1.4);
      const cz = (r + 0.5) * CELL + dr * (CELL / 2 - 1.4);
      this.batch.add('metal', this.cyl, '#d9661f', cx, 1.4, cz, 0, 0.1, 2.8, 0.1);
      const facing = Math.atan2(-dc, -dr);
      this.batch.add('metal', this.cyl, '#d9661f', cx, 2.9, cz, facing, 0.8, 0.08, 0.8, Math.PI / 2);
      this.batch.add('glass', this.cyl, '#8ea0b4', cx - dc * 0.03, 2.9, cz - dr * 0.03, facing, 0.7, 0.09, 0.7, Math.PI / 2);
      // A dark figure that can appear in the mirror (shown by the game, briefly).
      const ghost = new THREE.Mesh(this.plane, this.ghostMaterial());
      ghost.scale.set(0.34, 0.5, 1);
      ghost.position.set(cx - dc * 0.08, 2.86, cz - dr * 0.08);
      ghost.rotation.y = facing;
      ghost.visible = false;
      this.group.add(ghost);
      const f = new THREE.Vector3(-dc, 0, -dr).normalize();
      this.mirrors.push({ pos: new THREE.Vector3(cx, 2.9, cz), facing: f, ghost });
    }
  }

  private sensorLight(c: number, r: number): void {
    const east = !this.map.isStreet(c + 1, r);
    const dir = east ? 1 : -1;
    const wallX = (c + 0.5) * CELL + dir * (CELL / 2 - FENCE_INSET);
    const z = (r + 0.5) * CELL + 1.5;
    const mat = this.glowMaterial('#f4f8ff', 4);
    const head = new THREE.Mesh(this.box, mat);
    head.scale.set(0.3, 0.22, 0.4);
    head.position.set(wallX - dir * 0.25, 3.3, z);
    this.group.add(head);
    this.batch.add('metal', this.box, '#2c2d2f', wallX, 2.0, z, 0, 0.2, 2.6, 0.2);
    const source = this.addLight({
      id: 'sensor',
      lamp: new THREE.Vector3(wallX - dir * 0.5, 3.2, z),
      ground: new THREE.Vector2(wallX - dir * 2.6, z - 1),
      radius: 10.5,
      color: '#f4f8ff',
      intensity: 48,
      state: this.daylight ? 'off' : 'sensorOff',
      exposure: 1.25,
    });
    source.addGlow(mat);
  }

  private ghostMat: THREE.MeshBasicMaterial | null = null;

  /** Silhouette of a man in a cap, for mirror sightings. */
  private ghostMaterial(): THREE.MeshBasicMaterial {
    if (this.ghostMat) return this.ghostMat;
    const tex = canvasTexture(64, 96, (ctx) => {
      ctx.clearRect(0, 0, 64, 96);
      ctx.fillStyle = 'rgba(8,8,10,0.92)';
      ctx.beginPath();
      ctx.arc(32, 24, 11, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(18, 15, 28, 5);
      ctx.beginPath();
      ctx.moveTo(14, 96);
      ctx.quadraticCurveTo(14, 40, 32, 38);
      ctx.quadraticCurveTo(50, 40, 50, 96);
      ctx.fill();
    });
    this.ghostMat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false });
    this.disposables.push(this.ghostMat, tex);
    return this.ghostMat;
  }

  /** Vending machines against a north or south wall. */
  private vendingNS(c: number, r: number, id: string, sdr: number): void {
    const frontZ = (r + 0.5) * CELL + sdr * (CELL / 2 - FENCE_INSET - 0.04);
    const cx = (c + 0.5) * CELL;
    const rot = sdr > 0 ? Math.PI : 0;
    const frontTex = createVendingTexture();
    const glow = new THREE.MeshBasicMaterial({ map: frontTex });
    glow.color.setScalar(1.15);
    this.disposables.push(glow, frontTex);
    const bodies = ['#b8211d', '#e8e6e0', '#1f4fa8'];
    for (let i = 0; i < 2; i += 1) {
      const x = cx + (i === 0 ? -0.56 : 0.56);
      this.batch.add('metal', this.box, bodies[(c + r + i) % bodies.length], x, 0.92, frontZ + sdr * 0.4, 0, 1.05, 1.84, 0.8);
      const panel = new THREE.Mesh(this.plane, glow);
      panel.scale.set(0.86, 1.25, 1);
      panel.position.set(x, 1.12, frontZ - sdr * 0.012);
      panel.rotation.y = rot;
      this.group.add(panel);
    }
    const source = this.addLight({
      id,
      lamp: new THREE.Vector3(cx, 1.3, frontZ - sdr * 0.9),
      ground: new THREE.Vector2(cx, frontZ - sdr * 1.8),
      radius: 4.6,
      color: '#dcecff',
      intensity: 7,
      exposure: 0.75,
    });
    source.addGlow(glow);
    this.recyclingBins(cx + 1.45, frontZ - sdr * 0.3, cx + 2.1, frontZ - sdr * 0.3, rot);
    this.vendingSpots.push(new THREE.Vector3(cx, 1, frontZ));
  }

  /** Clumps of park trees filling a blocked cell. */
  private trees(c: number, r: number): void {
    const rng = this.rng;
    const b = this.batch;
    const count = 2 + Math.floor(rng() * 2);
    for (let i = 0; i < count; i += 1) {
      const tx = c * CELL + 1.6 + rng() * (CELL - 3.2);
      const tz = r * CELL + 1.6 + rng() * (CELL - 3.2);
      const h = 6 + rng() * 5;
      const shade = this.pick(['#16231a', '#1a2a1c', '#203020', '#1b261a']);
      b.add('wood', this.cyl, '#2b2018', tx, h * 0.25, tz, 0, 0.5, h * 0.5, 0.5);
      b.add('foliage', this.sphere, shade, tx, h * 0.62, tz, rng() * 3, 4.6, h * 0.55, 4.6);
      b.add('foliage', this.sphere, shade, tx + 0.6, h * 0.82, tz - 0.4, rng() * 3, 3.2, h * 0.38, 3.2);
    }
  }

  /**
   * A tall clipped hedge block (maze walls). It bulges out over the path by
   * HEDGE_SPILL on any side that faces a path (still inside the walking margin),
   * so corridors read as about 5 m wide instead of the cell's 8; a darker
   * plinth of trunks shows at the foot, and the top is lumpy.
   */
  private hedge(c: number, r: number): void {
    const b = this.batch;
    const rng = this.deco;
    const open = (dc: number, dr: number) => (this.map.isStreet(c + dc, r + dr) ? HEDGE_SPILL : 0);
    const x0 = c * CELL - open(-1, 0);
    const x1 = (c + 1) * CELL + open(1, 0);
    const z0 = r * CELL - open(0, -1);
    const z1 = (r + 1) * CELL + open(0, 1);
    const cx = (x0 + x1) / 2;
    const cz = (z0 + z1) / 2;
    const h = 2.6 + rng() * 0.25;
    const shade = this.pick(['#1b2b1d', '#1e301f', '#18271a']);
    b.add('foliage', this.box, shade, cx, h / 2, cz, 0, x1 - x0, h, z1 - z0);
    // Clipped top: a few low mounds so the skyline isn't ruler-straight.
    for (let i = 0; i < 4; i += 1) {
      const mx = x0 + 0.8 + rng() * (x1 - x0 - 1.6);
      const mz = z0 + 0.8 + rng() * (z1 - z0 - 1.6);
      b.add('foliage', this.sphere, this.pick(['#203421', '#1c2e1d', '#243a24']), mx, h, mz, rng() * 3, 2.2 + rng() * 1.6, 0.5, 2.2 + rng() * 1.6);
    }
  }

  /** A two-storey shop facing the covered arcade. */
  private shop(c: number, r: number): void {
    const b = this.batch;
    const cx = (c + 0.5) * CELL;
    const cz = (r + 0.5) * CELL;
    const h = 7 + this.rng() * 1.5;
    b.add('wall', this.box, this.pick(['#6d655c', '#5f5a55', '#7a6f62', '#575049']), cx, h / 2, cz, 0, CELL - 0.1, h, CELL - 0.1);
    b.add('roof', this.box, '#2c2a28', cx, h + 0.1, cz, 0, CELL, 0.2, CELL);
    for (const sd of SIDES) {
      const ach = this.map.char(c + sd.dc, r + sd.dr);
      if (!ARCADE.has(ach)) continue;
      const fx = cx + sd.dc * (CELL / 2 + 0.02);
      const fz = cz + sd.dr * (CELL / 2 + 0.02);
      // Closed shutter (corrugated grey) on every arcade-facing ground floor.
      b.add('metal', this.plane, '#5b5e62', fx, 1.6, fz, sd.rot, CELL - 1.2, 3.0, 1);
      // Upper-floor sign band and windows.
      b.add('concrete', this.plane, this.pick(['#2c3b52', '#4a2b2b', '#2f4434', '#4b4130']), fx + sd.dc * 0.01, 3.9, fz + sd.dr * 0.01, sd.rot, CELL - 1, 0.7, 1);
      b.add(this.rng() < 0.3 ? 'litWindow' : 'glass', this.plane, this.pick(WINDOW_LIT), fx, 5.6, fz, sd.rot, CELL - 3, 1.1, 1);
    }
  }

  /**
   * Covered shopping arcade: a roof over every arcade cell, ceiling lamps on
   * 'Y' (and 'W', which die like 'F' streetlights), and open, lit shops on
   * 'Q' whose shutters roll down as she passes.
   */
  private buildArcade(): void {
    const map = this.map;
    const b = this.batch;
    for (let r = 0; r < map.height; r += 1) {
      for (let c = 0; c < map.width; c += 1) {
        const ch = map.char(c, r);
        if (!ARCADE.has(ch)) continue;
        const cx = (c + 0.5) * CELL;
        const cz = (r + 0.5) * CELL;
        b.add('glass', this.box, '#1b2028', cx, 6.4, cz, 0, CELL, 0.12, CELL);
        b.add('metal', this.box, '#3a3d42', cx, 6.3, cz - CELL / 2 + 0.1, 0, CELL, 0.3, 0.2);
        if (ch === 'Y' || ch === 'W') {
          const id = ch === 'W' ? `F-${c}-${r}` : `Y-${c}-${r}`;
          const mat = this.glowMaterial('#e6f0ff', 3);
          const tube = new THREE.Mesh(this.box, mat);
          tube.scale.set(2.4, 0.08, 0.14);
          tube.position.set(cx, 6.05, cz);
          this.group.add(tube);
          const src = this.addLight({
            id,
            lamp: new THREE.Vector3(cx, 5.8, cz),
            ground: new THREE.Vector2(cx, cz),
            radius: 7.5,
            color: '#e1ebff',
            intensity: 15,
          });
          src.addGlow(mat);
        }
        if (ch === 'Q') {
          // A shop still open on the first walled side.
          const sd = SIDES.find((s2) => !map.isStreet(c + s2.dc, r + s2.dr) && s2.dc !== 0) ?? SIDES.find((s2) => !map.isStreet(c + s2.dc, r + s2.dr));
          if (!sd) continue;
          const fx = cx + sd.dc * (CELL / 2 - 0.05);
          const fz = cz + sd.dr * (CELL / 2 - 0.05);
          const tex = createShopTexture(this.rng());
          const front = this.texturedMesh(tex, CELL - 1.4, 2.8, 1.25);
          front.position.set(fx, 1.5, fz);
          front.rotation.y = sd.rot + Math.PI;
          this.group.add(front);
          const shutterMat = new THREE.MeshStandardMaterial({ color: '#6a6d71', roughness: 0.6, metalness: 0.3, side: THREE.DoubleSide });
          this.disposables.push(shutterMat);
          const shutter = new THREE.Mesh(this.plane, shutterMat);
          shutter.position.set(fx - sd.dc * 0.06, 3.15, fz - sd.dr * 0.06);
          shutter.rotation.y = sd.rot + Math.PI;
          shutter.scale.set(CELL - 1.2, 0.06, 1);
          this.group.add(shutter);
          const id = `Q-${c}-${r}`;
          this.shutters.set(id, { mesh: shutter, open: 1, target: 1 });
          const src = this.addLight({
            id,
            lamp: new THREE.Vector3(fx - sd.dc * 1.4, 2.4, fz - sd.dr * 1.4),
            ground: new THREE.Vector2(fx - sd.dc * 2.6, fz - sd.dr * 2.6),
            radius: 6.5,
            color: this.pick(['#ffd9a8', '#fff1d6', '#ffcf9a']),
            intensity: 16,
          });
          src.addGlow(front.material as THREE.MeshBasicMaterial);
        }
      }
    }
  }

  /** River water, railings along its banks and on bridges. */
  private buildWater(): void {
    const map = this.map;
    const b = this.batch;
    for (let r = 0; r < map.height; r += 1) {
      for (let c = 0; c < map.width; c += 1) {
        if (map.char(c, r) !== 'w') continue;
        b.add('water', this.box, '#101a24', (c + 0.5) * CELL, -0.15, (r + 0.5) * CELL, 0, CELL, 0.3, CELL);
        for (const sd of SIDES) {
          const nc = c + sd.dc;
          const nr = r + sd.dr;
          if (!map.isStreet(nc, nr)) continue;
          // Railing just inside the street/bridge edge.
          const along = sd.dc !== 0;
          const x = along ? (sd.dc > 0 ? (c + 1) * CELL + 0.25 : c * CELL - 0.25) : (c + 0.5) * CELL;
          const z = along ? (r + 0.5) * CELL : sd.dr > 0 ? (r + 1) * CELL + 0.25 : r * CELL - 0.25;
          b.add('metal', this.box, '#3d4247', x, 1.0, z, 0, along ? 0.06 : CELL, 0.06, along ? CELL : 0.06);
          b.add('metal', this.box, '#3d4247', x, 0.55, z, 0, along ? 0.04 : CELL, 0.04, along ? CELL : 0.04);
          for (let k = -3; k <= 3; k += 1) {
            b.add('metal', this.box, '#2f3337', x + (along ? 0 : k * 1.2), 0.5, z + (along ? k * 1.2 : 0), 0, 0.06, 1.0, 0.06);
          }
        }
      }
    }
  }

  /** Park benches on 'n' cells (they block her path). */
  private buildBenches(): void {
    for (const { c, r } of this.map.find('n')) {
      const cx = (c + 0.5) * CELL;
      const cz = (r + 0.5) * CELL;
      this.batch.add('wood', this.box, '#4a3626', cx, 0.45, cz, 0, 1.9, 0.08, 0.5);
      this.batch.add('wood', this.box, '#4a3626', cx, 0.8, cz + 0.24, 0, 1.9, 0.4, 0.06);
      for (const k of [-0.8, 0.8]) this.batch.add('metal', this.box, '#26282b', cx + k, 0.22, cz, 0, 0.06, 0.45, 0.45);
      this.map.addBlocker(cx, cz, 1.1);
    }
  }

  /**
   * Railway: 'r' cells are track (fenced off, see-through), 'K' cells are the
   * level crossing with gates, and 'e' cells are an embankment for a line
   * that crosses over the underpass. The track row also carries the train.
   */
  private buildRailway(): void {
    const map = this.map;
    const b = this.batch;
    const rows = new Set<number>();
    for (let r = 0; r < map.height; r += 1) {
      for (let c = 0; c < map.width; c += 1) {
        const ch = map.char(c, r);
        if (ch === 'e') {
          // Embankment wall; the track on top is out of sight.
          b.add('concrete', this.box, '#57544e', (c + 0.5) * CELL, 2.2, (r + 0.5) * CELL, 0, CELL, 4.4, CELL);
          b.add('metal', this.box, '#2d3033', (c + 0.5) * CELL, 5.0, (r + 0.5) * CELL, 0, CELL, 1.2, 0.08);
          continue;
        }
        if (ch !== 'r' && ch !== 'K') continue;
        rows.add(r);
        const cz = (r + 0.5) * CELL;
        const cx = (c + 0.5) * CELL;
        for (const dz of [-0.75, 0.75]) b.add('metal', this.box, '#7c7f84', cx, 0.09, cz + dz, 0, CELL, 0.14, 0.09);
        if (ch === 'r') {
          for (let k = 0; k < 5; k += 1) b.add('wood', this.box, '#3a2e25', c * CELL + 0.8 + k * 1.6, 0.04, cz, 0, 0.28, 0.08, 2.4);
          // Fence along any street that runs beside the track.
          for (const sd of SIDES) {
            if (sd.dc !== 0 || !map.isStreet(c, r + sd.dr) || map.char(c, r + sd.dr) === 'K') continue;
            const fz = sd.dr > 0 ? (r + 1) * CELL - 0.1 : r * CELL + 0.1;
            b.add('metal', this.box, '#3b3f44', cx, 1.6, fz, 0, CELL, 0.05, 0.05);
            b.add('metal', this.box, '#3b3f44', cx, 0.9, fz, 0, CELL, 0.04, 0.04);
            for (let k = -3; k <= 3; k += 1) b.add('metal', this.box, '#2f3337', cx + k * 1.2, 0.8, fz, 0, 0.06, 1.6, 0.06);
          }
        }
      }
    }
    const kCells = map.find('K');
    if (kCells.length === 0) return;

    // Gates: an arm on each side of the tracks, pivoting at a striped post.
    type Arm = { pivot: THREE.Group };
    const arms: Arm[] = [];
    const lampsA: THREE.MeshBasicMaterial[] = [];
    const lampsB: THREE.MeshBasicMaterial[] = [];
    const warnLights: LightSource[] = [];
    const armMat = new THREE.MeshStandardMaterial({ color: '#d8d2c4', roughness: 0.6 });
    const stripeMat = new THREE.MeshStandardMaterial({ color: '#c9a227', roughness: 0.6 });
    this.disposables.push(armMat, stripeMat);
    for (const { c, r } of kCells) {
      const cx = (c + 0.5) * CELL;
      const cz = (r + 0.5) * CELL;
      for (const side of [-1, 1]) {
        // Post on the west edge for the north gate, east edge for the south gate.
        const px = cx + side * (CELL / 2 - 0.9);
        const pz = cz + side * (CELL / 2 - 0.4);
        b.add('metal', this.box, '#e8e4da', px, 1.6, pz, 0, 0.18, 3.2, 0.18);
        b.add('metal', this.box, '#141414', px, 1.2, pz, 0, 0.2, 0.3, 0.2);
        b.add('metal', this.box, '#141414', px, 2.0, pz, 0, 0.2, 0.3, 0.2);
        // Crossed "X" sign and twin red lamps facing the road.
        b.add('metal', this.box, '#e3ddcf', px, 3.0, pz + side * 0.12, 0, 1.1, 0.16, 0.04, 0, Math.PI / 4);
        b.add('metal', this.box, '#e3ddcf', px, 3.0, pz + side * 0.12, 0, 1.1, 0.16, 0.04, 0, -Math.PI / 4);
        for (const [k, list] of [[-0.32, lampsA], [0.32, lampsB]] as const) {
          const m = this.glowMaterial('#ff2a1a', 3);
          m.color.multiplyScalar(0.08);
          const lamp = new THREE.Mesh(this.sphere, m);
          lamp.scale.setScalar(0.26);
          lamp.position.set(px + k, 2.45, pz + side * 0.16);
          this.group.add(lamp);
          list.push(m);
        }
        const pivot = new THREE.Group();
        pivot.position.set(px, 1.05, pz);
        const arm = new THREE.Mesh(this.box, armMat);
        arm.scale.set(CELL - 1.4, 0.12, 0.12);
        arm.position.x = -side * (CELL - 1.4) / 2;
        pivot.add(arm);
        for (let k = 0; k < 4; k += 1) {
          const st = new THREE.Mesh(this.box, stripeMat);
          st.scale.set(0.5, 0.13, 0.13);
          st.position.x = -side * (0.9 + k * 1.5);
          pivot.add(st);
        }
        pivot.rotation.z = side * 1.45;
        this.group.add(pivot);
        arms.push({ pivot });
      }
      warnLights.push(
        this.addLight({
          id: `X-${c}-${r}`,
          lamp: new THREE.Vector3(cx, 2.6, cz),
          ground: new THREE.Vector2(cx, cz),
          radius: 7,
          color: '#ff3020',
          intensity: 14,
          state: 'off',
          exposure: 0.4,
        }),
      );
    }

    // The train: five lit carriages, parked out of sight until needed.
    const row = kCells[0].r;
    const train = new THREE.Group();
    const bodyMat = new THREE.MeshStandardMaterial({ color: '#b9bec4', roughness: 0.35, metalness: 0.6 });
    const stripe = new THREE.MeshStandardMaterial({ color: '#2f7d4a', roughness: 0.5 });
    const windowMat = this.glowMaterial('#d8cdb4', 0.75);
    const headMat = this.glowMaterial('#fff6e0', 2.2);
    this.disposables.push(bodyMat, stripe);
    const CAR = 18;
    const CARS = 5;
    for (let i = 0; i < CARS; i += 1) {
      const body = new THREE.Mesh(this.box, bodyMat);
      body.scale.set(CAR - 0.6, 3.3, 2.9);
      body.position.set(i * CAR + CAR / 2, 2.05, 0);
      train.add(body);
      const band = new THREE.Mesh(this.box, stripe);
      band.scale.set(CAR - 0.6, 0.3, 2.94);
      band.position.set(i * CAR + CAR / 2, 1.2, 0);
      train.add(band);
      const win = new THREE.Mesh(this.box, windowMat);
      win.scale.set(CAR - 3, 0.8, 2.96);
      win.position.set(i * CAR + CAR / 2, 2.55, 0);
      train.add(win);
    }
    for (const end of [0, CARS * CAR]) {
      for (const dz of [-0.9, 0.9]) {
        const head = new THREE.Mesh(this.sphere, headMat);
        head.scale.setScalar(0.32);
        head.position.set(end, 1.4, dz);
        train.add(head);
      }
    }
    train.position.set(-999, 0, (row + 0.5) * CELL);
    train.visible = false;
    this.group.add(train);
    const length = CARS * CAR;
    const kx = kCells.map((k) => (k.c + 0.5) * CELL);
    const minX = Math.min(...kx);
    const maxX = Math.max(...kx);

    let warning = false;
    let gatesDown = false;
    let gate = 0; // 0 = up, 1 = down
    let trainState: TrainState = 'none';
    let trainDir = 1;
    let flash = 0;
    this.crossing = {
      cells: kCells,
      setWarning: (on) => {
        warning = on;
        if (!on) {
          for (const m of [...lampsA, ...lampsB]) m.color.set('#ff2a1a').multiplyScalar(3 * 0.08);
          for (const l of warnLights) l.setState('off');
        }
      },
      setGates: (down) => {
        gatesDown = down;
      },
      runTrain: () => {
        trainDir = -trainDir;
        trainState = 'coming';
        train.visible = true;
        // Start ~90 m from the crossing so the headlights come out of the dark.
        train.position.x = trainDir > 0 ? minX - 90 - length : maxX + 90;
      },
      get train() {
        return trainState;
      },
    };
    this.crossingReset = () => {
      this.crossing?.setWarning(false);
      gatesDown = false;
      gate = 0;
      trainState = 'none';
      train.visible = false;
      train.position.x = -999;
    };
    this.crossingUpdate = (dt) => {
      gate += Math.sign((gatesDown ? 1 : 0) - gate) * Math.min(Math.abs((gatesDown ? 1 : 0) - gate), dt / 2.2);
      for (let i = 0; i < arms.length; i += 1) {
        const side = i % 2 === 0 ? -1 : 1;
        arms[i].pivot.rotation.z = side * 1.45 * (1 - gate);
      }
      if (warning) {
        flash += dt;
        const a = Math.floor(flash * 2.3) % 2 === 0;
        for (const m of lampsA) m.color.set('#ff2a1a').multiplyScalar(a ? 3 : 0.24);
        for (const m of lampsB) m.color.set('#ff2a1a').multiplyScalar(a ? 0.24 : 3);
        for (const l of warnLights) l.setState(a ? 'on' : 'off');
      }
      if (trainState === 'coming' || trainState === 'passing') {
        train.position.x += trainDir * 19 * dt;
        const tail = train.position.x;
        const head = tail + length;
        const overlapping = head > minX - 6 && tail < maxX + 6;
        if (overlapping) trainState = 'passing';
        else if (trainState === 'passing') trainState = 'gone';
      }
      if (trainState === 'gone' && (train.position.x > maxX + 120 || train.position.x + length < minX - 120)) {
        train.visible = false;
      } else if (trainState === 'gone') {
        train.position.x += trainDir * 19 * dt;
      }
    };
  }

  /** Underpass ('u', with a flickering tube on 'U'): a low concrete roof under the embankment. */
  private buildUnderpass(): void {
    const map = this.map;
    const b = this.batch;
    for (let r = 0; r < map.height; r += 1) {
      for (let c = 0; c < map.width; c += 1) {
        const ch = map.char(c, r);
        if (ch !== 'u' && ch !== 'U') continue;
        const cx = (c + 0.5) * CELL;
        const cz = (r + 0.5) * CELL;
        b.add('concrete', this.box, '#4a4843', cx, 3.6, cz, 0, CELL, 0.5, CELL);
        if (map.char(c, r - 1) === 'e' || map.char(c, r + 1) === 'e' || map.char(c - 1, r) === 'e' || map.char(c + 1, r) === 'e') {
          b.add('concrete', this.box, '#57544e', cx, 4.6, cz, 0, CELL, 1.6, CELL);
        }
        // Tiled side walls, streaked.
        for (const sd of SIDES) {
          if (map.isStreet(c + sd.dc, r + sd.dr)) continue;
          const wx = cx + sd.dc * (CELL / 2 - 0.15);
          const wz = cz + sd.dr * (CELL / 2 - 0.15);
          b.add('concrete', this.box, '#6b6a63', wx, 1.7, wz, 0, sd.dc !== 0 ? 0.3 : CELL, 3.4, sd.dc !== 0 ? CELL : 0.3);
        }
        if (ch === 'U') {
          const mat = this.glowMaterial('#e6f0ff', 3);
          const tube = new THREE.Mesh(this.box, mat);
          tube.scale.set(0.14, 0.08, 2.2);
          tube.position.set(cx, 3.3, cz);
          this.group.add(tube);
          const src = this.addLight({
            id: `U-${c}-${r}`,
            lamp: new THREE.Vector3(cx, 3.1, cz),
            ground: new THREE.Vector2(cx, cz),
            radius: 6.5,
            color: '#dfe9ff',
            intensity: 12,
            state: 'broken',
          });
          src.addGlow(mat);
        }
      }
    }
  }

  /** Doorstep candles ('k' cells): dark until a blackout, then lit by whoever lives there. */
  private buildCandles(): void {
    for (const { c, r } of this.map.find('k')) {
      const sd = SIDES.find((s) => !this.map.isStreet(c + s.dc, r + s.dr));
      if (!sd) continue;
      const x = (c + 0.5) * CELL + sd.dc * (CELL / 2 - 1.1);
      const z = (r + 0.5) * CELL + sd.dr * (CELL / 2 - 1.1);
      this.batch.add('concrete', this.box, '#5a5650', x, 0.25, z, 0, 0.6, 0.5, 0.6);
      const mat = this.glowMaterial('#ffb35c', 2.6);
      const flame = new THREE.Mesh(this.sphere, mat);
      flame.scale.set(0.12, 0.2, 0.12);
      flame.position.set(x, 0.62, z);
      this.group.add(flame);
      const jar = new THREE.Mesh(this.cyl, this.glowMaterial('#ff9a3c', 0.6));
      jar.scale.set(0.24, 0.3, 0.24);
      jar.position.set(x, 0.65, z);
      this.group.add(jar);
      const src = this.addLight({
        id: `K-${c}-${r}`,
        lamp: new THREE.Vector3(x, 0.9, z),
        ground: new THREE.Vector2(x - sd.dc * 0.8, z - sd.dr * 0.8),
        radius: 5.2,
        color: '#ffae5a',
        intensity: 7,
        state: 'off',
        exposure: 0.8,
      });
      src.addGlow(mat);
      src.addGlow(jar.material as THREE.MeshBasicMaterial);
    }
  }

  private blackout(x: number, z: number): void {
    for (const light of this.lights.sources) {
      const d = Math.hypot(light.lamp.x - x, light.lamp.z - z);
      if (light.id.startsWith('K-')) {
        this.pendingLights.push({ at: this.clock + 6 + this.rng() * 8, light, state: 'candle' });
      } else if (!light.id.startsWith('X-')) {
        // The dark sweeps out from her at ~45 m/s.
        this.pendingLights.push({ at: this.clock + d / 45, light, state: 'dying' });
      }
    }
    this.windowFade = { material: this.litWindow, t: 1 };
  }

  private buildWires(): void {
    const points: number[] = [];
    const heights = [7.9, 7.5, 7.1];
    for (let i = 0; i < this.poles.length; i += 1) {
      for (let j = i + 1; j < this.poles.length; j += 1) {
        const a = this.poles[i];
        const p = this.poles[j];
        const dx = p.x - a.x;
        const dz = p.z - a.z;
        const dist = Math.hypot(dx, dz);
        if (dist > 18.5 || dist < 2) continue;
        if (Math.abs(dx) > 3.2 && Math.abs(dz) > 3.2) continue;
        const nx = -dz / dist;
        const nz = dx / dist;
        // Crows sit in a row on the top wire of some spans.
        if (this.clutter() < 0.22) {
          const n = 1 + Math.floor(this.clutter() * 3);
          const t0 = 0.3 + this.clutter() * 0.3;
          for (let q = 0; q < n; q += 1) {
            const t = t0 + q * (0.6 / dist);
            this.perches.push({
              x: a.x + dx * t - nx * 0.55,
              y: heights[0] - Math.sin(t * Math.PI) * 0.55,
              z: a.z + dz * t - nz * 0.55,
              rot: Math.atan2(nx, nz) + (this.clutter() < 0.5 ? 0 : Math.PI),
              ground: false,
            });
          }
        }
        heights.forEach((h, k) => {
          const lateral = (k - 1) * 0.55;
          const segs = 8;
          for (let s = 0; s < segs; s += 1) {
            for (const t of [s / segs, (s + 1) / segs]) {
              const sag = Math.sin(t * Math.PI) * 0.55;
              points.push(a.x + dx * t + nx * lateral, h - sag, a.z + dz * t + nz * lateral);
            }
          }
        });
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
    const material = new THREE.LineBasicMaterial({ color: '#0a0b0d' });
    const lines = new THREE.LineSegments(geometry, material);
    this.group.add(lines);
    this.disposables.push(geometry, material);
  }

  // ---------------------------------------------------------------- landmarks

  private buildKonbini(): void {
    const b = this.batch;
    const [cell] = this.map.find('C');
    if (!cell) return;
    const x0 = cell.c * CELL + 0.2;
    const x1 = (cell.c + 1) * CELL - 0.2;
    const z0 = cell.r * CELL + 0.2;
    const z1 = (cell.r + 1) * CELL - 0.2;
    const cx = (x0 + x1) / 2;
    const cz = (z0 + z1) / 2;
    b.add('wall', this.box, '#d6d8d6', cx, 2.2, cz, 0, x1 - x0, 4.4, z1 - z0);
    b.add('roof', this.box, '#3a3d40', cx, 4.45, cz, 0, x1 - x0 + 0.2, 0.15, z1 - z0 + 0.2);

    const shop = this.texturedMesh(createKonbiniFrontTexture(), 6.6, 2.7, 1.05);
    shop.position.set(x0 - 0.02, 1.45, cz);
    shop.rotation.y = -Math.PI / 2;
    this.group.add(shop);
    const sign = this.texturedMesh(createKonbiniSignTexture(), 7.4, 0.9, 1.2);
    sign.position.set(x0 - 0.03, 3.45, cz);
    sign.rotation.y = -Math.PI / 2;
    this.group.add(sign);
    // Bins outside
    b.add('metal', this.box, '#3d5d8a', x0 - 0.5, 0.5, z0 + 0.8, 0, 0.6, 1.0, 1.4);

    const source = this.addLight({
      id: 'konbini',
      lamp: new THREE.Vector3(x0 - 1.4, 2.9, cz),
      ground: new THREE.Vector2(x0 - 3.2, cz),
      radius: 11.5,
      color: '#f1f7ff',
      intensity: 22,
    });
    source.addGlow(shop.material as THREE.MeshBasicMaterial);
    source.addGlow(sign.material as THREE.MeshBasicMaterial);
  }

  private buildStation(): void {
    const b = this.batch;
    const [cell] = this.map.find('E');
    if (!cell) return;
    const z0 = cell.r * CELL + 0.2;
    const cx = (cell.c + 0.5) * CELL;
    b.add('wall', this.box, '#8e928f', cx, 3.6, z0 + 6, 0, 40, 7.2, 12);
    b.add('roof', this.box, '#2a2c30', cx, 7.3, z0 + 6, 0, 41, 0.3, 13);
    // Elevated track behind
    b.add('concrete', this.box, '#5f615f', cx, 9.0, z0 + 14, 0, 140, 1.2, 6);
    for (let i = -6; i <= 6; i += 1) b.add('concrete', this.box, '#5a5c5a', cx + i * 11, 4.2, z0 + 14, 0, 1.4, 8.4, 2.2);

    const entrance = this.texturedMesh(createStationEntranceTexture(), 7, 3.1, 1.0);
    entrance.position.set(cx, 1.6, z0 - 0.02);
    entrance.rotation.y = Math.PI;
    this.group.add(entrance);
    const sign = this.texturedMesh(createStationSignTexture(), 9, 1.4, 1.15);
    sign.position.set(cx, 5.2, z0 - 0.03);
    sign.rotation.y = Math.PI;
    this.group.add(sign);

    const source = this.addLight({
      id: 'station',
      lamp: new THREE.Vector3(cx, 4.0, z0 - 1.6),
      ground: new THREE.Vector2(cx, z0 - 3.5),
      radius: 12.5,
      color: '#e8f0ff',
      intensity: 34,
    });
    source.addGlow(entrance.material as THREE.MeshBasicMaterial);
    source.addGlow(sign.material as THREE.MeshBasicMaterial);
  }

  private apartmentWindows(
    x: number,
    z: number,
    rot: number,
    span: number,
    floors: number[],
    litChance: number,
    alongX: boolean,
  ): void {
    const count = Math.max(1, Math.floor(span / 2.6));
    for (const f of floors) {
      for (let i = 0; i < count; i += 1) {
        const off = (i + 0.5) * (span / count) - span / 2;
        const px = alongX ? x + off : x;
        const pz = alongX ? z : z + off;
        const y = 1.5 + f * 3.1;
        const lit = this.rng() < litChance;
        this.batch.add(lit ? 'litWindow' : 'glass', this.plane, lit ? this.pick(WINDOW_LIT) : '#121820', px, y, pz, rot, 1.5, 1.2, 1);
      }
    }
  }

  private buildMaisonSakura(): void {
    const b = this.batch;
    const [cell] = this.map.find('A');
    if (!cell) return;
    const ax0 = cell.c * CELL;
    const ax1 = ax0 + CELL;
    const az0 = cell.r * CELL;
    const az1 = az0 + CELL;
    const color = '#b7a197';
    const H = 10;
    const W = 2 * CELL - 0.4;
    b.add('wall', this.box, color, ax0 + W / 2 + 0.2, H / 2, az0 - CELL / 2 + 0.1, 0, W, H, CELL - 0.2);
    b.add('wall', this.box, color, ax1 + CELL / 2 - 0.1, H / 2, (az0 + az1) / 2, 0, CELL - 0.2, H, CELL);
    b.add('wall', this.box, color, ax0 + W / 2 + 0.2, H / 2, az1 + CELL / 2 - 0.1, 0, W, H, CELL - 0.2);
    b.add('wall', this.box, color, (ax0 + ax1) / 2 + 0.1, 3.3 + (H - 3.3) / 2, (az0 + az1) / 2, 0, CELL - 0.2, H - 3.3, CELL);
    b.add('roof', this.box, '#3a3634', ax0 + W / 2 + 0.2, H + 0.15, (az0 + az1) / 2, 0, W + 0.2, 0.3, CELL * 3);
    this.apartmentWindows(ax0 + 0.15, az0 - CELL / 2, -Math.PI / 2, CELL - 1, [0, 1, 2], 0.5, false);
    this.apartmentWindows(ax0 + 0.15, az1 + CELL / 2, -Math.PI / 2, CELL - 1, [0, 1, 2], 0.5, false);
    this.apartmentWindows(ax0 + 0.15, (az0 + az1) / 2, -Math.PI / 2, CELL - 1, [1, 2], 0.6, false);

    // Bright, welcoming entrance — the wrong building.
    const door = this.texturedMesh(createGlassDoorTexture('#ffe2b8'), 2.4, 2.4, 1.0);
    door.position.set(ax1 - 0.12, 1.2, (az0 + az1) / 2);
    door.rotation.y = -Math.PI / 2;
    this.group.add(door);
    const sign = this.texturedMesh(createTextTexture('メゾン桜', 'MAISON SAKURA', '#2a1d1a', '#ffd6e0'), 4.6, 1.0, 0.95);
    sign.position.set(ax0 + 0.15, 3.95, (az0 + az1) / 2);
    sign.rotation.y = -Math.PI / 2;
    this.group.add(sign);
    const source = this.addLight({
      id: 'maison',
      lamp: new THREE.Vector3((ax0 + ax1) / 2, 3.0, (az0 + az1) / 2),
      ground: new THREE.Vector2(ax0 + 1.5, (az0 + az1) / 2),
      radius: 7.5,
      color: '#ffd8a8',
      intensity: 18,
    });
    source.addGlow(door.material as THREE.MeshBasicMaterial);
    source.addGlow(sign.material as THREE.MeshBasicMaterial);
  }

  private buildKomorebiSo(): void {
    const b = this.batch;
    const [cell] = this.map.find('H');
    if (!cell) return;
    const hx0 = cell.c * CELL;
    const hx1 = hx0 + CELL;
    const hz0 = cell.r * CELL;
    const hz1 = hz0 + CELL;
    const color = '#7f7a70';
    const H = 9.2;
    b.add('wall', this.box, color, (hx0 + hx1) / 2, H / 2, hz0 - 7, 0, CELL * 3 - 0.4, H, 14);
    b.add('wall', this.box, color, hx0 - CELL / 2 + 0.1, H / 2, (hz0 + hz1) / 2 - 0.1, 0, CELL - 0.2, H, CELL - 0.2);
    b.add('wall', this.box, color, hx1 + CELL / 2 - 0.1, H / 2, (hz0 + hz1) / 2 - 0.1, 0, CELL - 0.2, H, CELL - 0.2);
    b.add('wall', this.box, color, (hx0 + hx1) / 2, 3.2 + (H - 3.2) / 2, (hz0 + hz1) / 2 - 0.1, 0, CELL, H - 3.2, CELL - 0.2);
    b.add('roof', this.box, '#2c2c2c', (hx0 + hx1) / 2, H + 0.15, hz0 - 3, 0, CELL * 3, 0.3, 22);
    // South-facing windows; only a couple of neighbours are still awake.
    this.apartmentWindows(hx0 - CELL / 2, hz1 - 0.15, 0, CELL - 1, [0, 1, 2], 0.2, true);
    this.apartmentWindows(hx1 + CELL / 2, hz1 - 0.15, 0, CELL - 1, [0, 1, 2], 0.25, true);
    this.apartmentWindows((hx0 + hx1) / 2, hz1 - 0.15, 0, CELL - 1, [1, 2], 0.3, true);
    // Exterior walkway rails
    for (const f of [1, 2]) {
      b.add('metal', this.box, '#4b4d50', (hx0 + hx1) / 2, f * 3.1 - 0.1, hz1 + 0.5, 0, CELL * 3 - 0.6, 1.0, 0.06);
      b.add('concrete', this.box, '#5b5850', (hx0 + hx1) / 2, f * 3.1 - 0.62, hz1 + 0.35, 0, CELL * 3 - 0.6, 0.12, 0.9);
    }

    b.add('wood', this.plane, '#3b2a20', (hx0 + hx1) / 2, 1.1, hz0 + 0.04, 0, 1.1, 2.2, 1);
    b.add('metal', this.box, '#8b8f93', hx0 + 0.2, 1.2, hz0 + 2.2, 0, 0.3, 1.2, 1.6); // mailboxes
    const sign = this.texturedMesh(createTextTexture('こもれび荘', 'KOMOREBI-SO', '#1d1f1c', '#cfd8c8'), 3.4, 0.75, 0.8, true);
    sign.position.set((hx0 + hx1) / 2, 3.75, hz1 - 0.13);
    this.group.add(sign);
    const lampMat = this.glowMaterial('#d9eaff', 2.4);
    const lamp = new THREE.Mesh(this.box, lampMat);
    lamp.scale.set(0.4, 0.1, 0.4);
    lamp.position.set((hx0 + hx1) / 2, 3.15, (hz0 + hz1) / 2);
    this.group.add(lamp);
    const source = this.addLight({
      id: 'home-entrance',
      lamp: new THREE.Vector3((hx0 + hx1) / 2, 3.0, (hz0 + hz1) / 2),
      ground: new THREE.Vector2((hx0 + hx1) / 2, (hz0 + hz1) / 2),
      radius: 5,
      color: '#d9eaff',
      intensity: 10,
      state: 'off',
    });
    source.addGlow(lampMat);
  }

  private buildShrine(): void {
    const b = this.batch;
    const map = this.map;
    const red = '#a8321d';
    for (const gate of map.find('G')) {
      const gx = (gate.c + 1) * CELL - 0.8;
      const gz = (gate.r + 0.5) * CELL;
      for (const k of [-1, 1]) {
        b.add('wood', this.cyl, red, gx, 2.3, gz + k * 2.55, 0, 0.36, 4.6, 0.36);
        b.add('concrete', this.cyl, '#2a2a28', gx, 0.15, gz + k * 2.55, 0, 0.5, 0.3, 0.5);
      }
      b.add('wood', this.box, red, gx, 3.85, gz, 0, 0.26, 0.24, 6.0);
      b.add('wood', this.box, red, gx, 4.62, gz, 0, 0.36, 0.3, 7.2);
      b.add('wood', this.box, '#141414', gx, 4.86, gz, 0, 0.42, 0.16, 7.6);
      b.add('wood', this.box, red, gx, 4.25, gz, 0, 0.16, 0.6, 0.3);
    }

    // Stone lanterns (unlit) along the path, alternating sides.
    const pathCells = map.find('p');
    for (const { c, r } of pathCells) {
      const vertical = !map.isStreet(c - 1, r) && !map.isStreet(c + 1, r);
      const side = (c + r) % 2 === 0 ? -1 : 1;
      const px = (c + 0.5) * CELL + (vertical ? side * 1.7 : 0);
      const pz = (r + 0.5) * CELL + (vertical ? 0 : side * 1.7);
      b.add('concrete', this.box, '#56574f', px, 0.2, pz, 0, 0.6, 0.4, 0.6);
      b.add('concrete', this.cyl, '#5b5c54', px, 0.85, pz, 0, 0.22, 0.9, 0.22);
      b.add('concrete', this.box, '#4f5049', px, 1.45, pz, 0, 0.5, 0.42, 0.5);
      b.add('concrete', this.cone, '#4a4b44', px, 1.85, pz, Math.PI / 4, 0.95, 0.42, 0.95);
    }

    // A small shrine hall in the first grove cell beside the path, facing it.
    for (const { c, r } of pathCells) {
      for (const sd of SIDES) {
        const hc = c - sd.dc;
        const hr = r - sd.dr;
        if (map.isStreet(hc, hr) || !this.isGrove(hc, hr)) continue;
        // (dc, dr) points from the hall toward the path.
        const dc = sd.dc;
        const dr = sd.dr;
        const rot = Math.atan2(-dr, dc);
        const cx = (hc + 0.5) * CELL + dc * 0.4;
        const cz = (hr + 0.5) * CELL + dr * 0.4;
        const at = (fwd: number, side: number): [number, number] => [cx + dc * fwd - dr * side, cz + dr * fwd + dc * side];
        b.add('wood', this.box, '#3a2a1e', cx, 1.4, cz, rot, 2.6, 2.2, 3.2);
        b.add('concrete', this.box, '#4c4c45', cx, 0.2, cz, rot, 3.4, 0.4, 4.0);
        b.add('roof', this.prism, '#2a2d2a', cx, 2.5 + 1.1 / 3, cz, rot, 4.2 / 1.732, 4.4, 1.1 / 1.5, -Math.PI / 2);
        const [rx, rz] = at(1.35, 0);
        b.add('wood', this.box, '#d8cfb8', rx, 2.35, rz, rot, 0.06, 0.08, 2.6); // shimenawa rope
        for (let i = -1; i <= 1; i += 1) {
          const [sx, sz] = at(1.38, i * 0.8);
          b.add('wood', this.box, '#e9e3d2', sx, 2.12, sz, rot, 0.02, 0.36, 0.14);
        }
        return;
      }
    }
  }

  private buildRoadWorks(): void {
    const b = this.batch;
    const [cell] = this.map.find('X');
    if (!cell) return;
    const x0 = cell.c * CELL + FENCE_INSET;
    const x1 = (cell.c + 1) * CELL - FENCE_INSET;
    const cx = (x0 + x1) / 2;
    const zb = cell.r * CELL + CELL * 0.56 - 0.25;
    const stripes = createStripeTexture();
    const boardMat = new THREE.MeshStandardMaterial({ map: stripes, roughness: 0.6 });
    this.disposables.push(stripes, boardMat);
    for (const k of [-1, 1]) {
      const bx = cx + k * 1.35;
      for (const y of [0.55, 0.95]) {
        const board = new THREE.Mesh(this.box, boardMat);
        board.scale.set(2.5, 0.28, 0.05);
        board.position.set(bx, y, zb);
        board.castShadow = true;
        this.group.add(board);
      }
      for (const lx of [-1.1, 1.1]) b.add('metal', this.box, '#d8d8d0', bx + lx, 0.55, zb, 0, 0.06, 1.1, 0.5);
    }
    // Cones
    for (let i = 0; i < 3; i += 1) {
      const x = x0 + 0.6 + i * ((x1 - x0 - 1.2) / 2);
      this.prop('cone', x, zb - 0.6, i * 0.7);
    }
    // The crew's fuel can for the generator.
    this.prop('gasCan', x1 - 1.1, zb - 1.3, 0.4);
    // Dug-up road and spoil heap beyond the barrier.
    b.add('wood', this.box, '#2b2219', cx, 0.04, zb - 2.4, 0, x1 - x0, 0.1, 3.4);
    b.add('wood', this.sphere, '#3a2e22', cx - 1.2, 0.3, zb - 3.2, 0, 2.4, 1.0, 1.8);
    b.add('metal', this.box, '#3e4042', cx + 1.0, 0.05, zb - 1.6, 0, 1.8, 0.04, 1.4);

    const sign = this.texturedMesh(createTextTexture('工事中', '通行止め  ROAD CLOSED', '#f7f1e1', '#b11f16'), 1.3, 1.7, 1.1, false);
    sign.position.set(cx, 1.25, zb + 0.06);
    this.group.add(sign);
    b.add('metal', this.box, '#dcdcd4', cx, 0.4, zb + 0.02, 0, 0.06, 0.8, 0.06);

    // Blinking warning lamps on the boards.
    const blinkMat = this.glowMaterial('#ff3a1e', 3.4);
    for (const k of [-2.4, 0.3, 2.4]) {
      const lamp = new THREE.Mesh(this.sphere, blinkMat);
      lamp.scale.setScalar(0.16);
      lamp.position.set(cx + k, 1.18, zb);
      this.group.add(lamp);
    }
    const blink = this.addLight({
      id: 'roadworks-blink',
      lamp: new THREE.Vector3(cx, 1.4, zb + 0.6),
      ground: new THREE.Vector2(cx, zb + 1.5),
      radius: 5.5,
      color: '#ff4a2a',
      intensity: 7,
      state: 'blink',
      exposure: 0.5,
    });
    blink.addGlow(blinkMat);

    // Tripod work light — the brightest thing on the avenue.
    const tx = x1 - 0.4;
    const tz = zb - 0.4;
    for (let i = 0; i < 3; i += 1) {
      const a = (i / 3) * Math.PI * 2;
      b.add('metal', this.cyl, '#2d2e30', tx + Math.cos(a) * 0.25, 1.5, tz + Math.sin(a) * 0.25, 0, 0.05, 3.0, 0.05, Math.sin(a) * 0.17, -Math.cos(a) * 0.17);
    }
    const workMat = this.glowMaterial('#fff6e0', 4);
    const head = new THREE.Mesh(this.box, workMat);
    head.scale.set(0.5, 0.36, 0.14);
    head.position.set(tx, 3.1, tz + 0.1);
    head.rotation.x = -0.4;
    this.group.add(head);
    const work = this.addLight({
      id: 'roadworks-work',
      lamp: new THREE.Vector3(tx - 0.5, 3.0, tz + 1.0),
      ground: new THREE.Vector2(cx, zb + 2.5),
      radius: 9,
      color: '#fff1d6',
      intensity: 30,
    });
    work.addGlow(workMat);
  }

  private buildSky(): void {
    if (this.overcast) return;
    const positions: number[] = [];
    const rng = createSeededRandom(9);
    for (let i = 0; i < 700; i += 1) {
      const theta = rng() * Math.PI * 2;
      const phi = Math.acos(0.15 + rng() * 0.85);
      const radius = 380;
      positions.push(
        (this.map.width * CELL) / 2 + Math.sin(phi) * Math.cos(theta) * radius,
        Math.cos(phi) * radius,
        (this.map.height * CELL) / 2 + Math.sin(phi) * Math.sin(theta) * radius,
      );
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    const material = new THREE.PointsMaterial({ color: '#9fb0d0', size: 1.4, sizeAttenuation: false, fog: false });
    const stars = new THREE.Points(geometry, material);
    stars.renderOrder = -1;
    this.group.add(stars);
    this.disposables.push(geometry, material);

    const moonTex = createMoonTexture();
    const moonMat = new THREE.SpriteMaterial({ map: moonTex, fog: false, transparent: true, depthWrite: false });
    moonMat.color.setScalar(1.6);
    const moon = new THREE.Sprite(moonMat);
    moon.scale.setScalar(26);
    moon.position.set(-60, 170, -200);
    this.group.add(moon);
    this.disposables.push(moonTex, moonMat);
  }
}

// ------------------------------------------------------------------ canvas textures

/** How far a maze hedge spills over the path beside it (m); less than WALK_MARGIN so she never walks into it. */
const HEDGE_SPILL = 1.4;

/**
 * One bamboo twig: seven narrow, tapering leaves fanning off a short stem that
 * points along +x, drooping toward their tips. Each blade is folded slightly
 * along its midrib so its two halves catch the light differently. About 1 m long at scale 1.
 */
function createLeafSpray(): THREE.BufferGeometry {
  const pos: number[] = [];
  const tri = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3) => pos.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
  const leaves: [number, number, number][] = [
    // [base along the twig, sideways angle, length]
    [0.12, 0.9, 0.42],
    [0.2, -0.8, 0.46],
    [0.3, 0.6, 0.5],
    [0.38, -0.55, 0.48],
    [0.48, 0.35, 0.46],
    [0.55, -0.3, 0.42],
    [0.62, 0.05, 0.4],
  ];
  // The twig itself: a thin sliver.
  const t0 = new THREE.Vector3(0, 0.008, 0);
  const t1 = new THREE.Vector3(0.62, -0.06, 0);
  tri(t0, t1, new THREE.Vector3(0, -0.008, 0.006));
  for (const [at, side, len] of leaves) {
    const base = new THREE.Vector3(at, -at * 0.1, 0);
    const dir = new THREE.Vector3(Math.cos(side), 0, Math.sin(side));
    const across = new THREE.Vector3(-dir.z, 0, dir.x);
    const w = 0.05;
    // u runs base to tip, s across the blade; the droop grows toward the tip and the edges sit below the midrib.
    const point = (u: number, s: number) => {
      const p = base.clone().addScaledVector(dir, u * len).addScaledVector(across, s * w);
      p.y -= u * u * len * 0.55 + Math.abs(s) * w * 0.35;
      return p;
    };
    const b0 = point(0, 0);
    const l = point(0.4, -1);
    const r = point(0.4, 1);
    const m = point(0.4, 0);
    const tip = point(1, 0);
    tri(b0, l, m);
    tri(b0, m, r);
    tri(m, l, tip);
    tri(m, tip, r);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.computeVertexNormals();
  // Batched with other leaf geometry, which has UVs.
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array((pos.length / 3) * 2), 2));
  return geo;
}

function canvasTexture(w: number, h: number, draw: (ctx: CanvasRenderingContext2D) => void): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  draw(ctx);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}

const JP_FONT = '"Yu Gothic", "Meiryo", "Hiragino Sans", "Noto Sans JP", sans-serif';

function createShopTexture(seed: number): THREE.CanvasTexture {
  const kinds = [
    { bg: '#ffe9c4', ink: '#7a2b14', name: 'ラーメン', sub: 'RAMEN' },
    { bg: '#e8f4ff', ink: '#1d4e8a', name: '薬局', sub: 'PHARMACY' },
    { bg: '#fff6e0', ink: '#6a4a12', name: '喫茶', sub: 'KISSA' },
    { bg: '#f4e8ff', ink: '#4b2a7a', name: '本屋', sub: 'BOOKS' },
  ];
  const k = kinds[Math.floor(seed * kinds.length) % kinds.length];
  return canvasTexture(256, 112, (ctx) => {
    ctx.fillStyle = k.bg;
    ctx.fillRect(0, 0, 256, 112);
    ctx.fillStyle = 'rgba(0,0,0,0.08)';
    for (let i = 0; i < 6; i += 1) ctx.fillRect(16 + i * 40, 40, 28, 60);
    ctx.fillStyle = k.ink;
    ctx.fillRect(0, 0, 256, 30);
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold 20px ${JP_FONT}`;
    ctx.textAlign = 'center';
    ctx.fillText(`${k.name}  ${k.sub}`, 128, 22);
  });
}

function createVendingTexture(): THREE.CanvasTexture {
  return canvasTexture(128, 192, (ctx) => {
    ctx.fillStyle = '#eef6ff';
    ctx.fillRect(0, 0, 128, 192);
    const colors = ['#d22', '#2a6', '#fb2', '#26c', '#e73', '#555', '#9c3', '#c2a'];
    for (let row = 0; row < 3; row += 1) {
      ctx.fillStyle = '#c9d6e6';
      ctx.fillRect(6, 10 + row * 44 + 30, 116, 4);
      for (let i = 0; i < 6; i += 1) {
        ctx.fillStyle = colors[(row * 3 + i) % colors.length];
        ctx.fillRect(10 + i * 19, 14 + row * 44, 12, 26);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(10 + i * 19, 20 + row * 44, 12, 5);
      }
    }
    ctx.fillStyle = '#20252c';
    ctx.fillRect(20, 150, 88, 30);
  });
}

function createKonbiniFrontTexture(): THREE.CanvasTexture {
  return canvasTexture(512, 210, (ctx) => {
    ctx.fillStyle = '#f4f8ff';
    ctx.fillRect(0, 0, 512, 210);
    // Shelves seen through the glass
    for (let row = 0; row < 4; row += 1) {
      ctx.fillStyle = '#c4ccd8';
      ctx.fillRect(30, 40 + row * 38, 452, 5);
      for (let i = 0; i < 40; i += 1) {
        ctx.fillStyle = `hsl(${(i * 47 + row * 31) % 360}, 55%, 60%)`;
        ctx.fillRect(32 + i * 11.2, 22 + row * 38, 8, 17);
      }
    }
    // Mullions and the door
    ctx.fillStyle = '#7d858f';
    for (let i = 0; i <= 4; i += 1) ctx.fillRect(i * 127, 0, 5, 210);
    ctx.fillStyle = 'rgba(30,40,50,0.15)';
    ctx.fillRect(195, 20, 120, 190);
    ctx.fillStyle = '#ff7b2a';
    ctx.font = `bold 22px ${JP_FONT}`;
    ctx.fillText('いらっしゃいませ', 320, 196);
  });
}

function createKonbiniSignTexture(): THREE.CanvasTexture {
  return canvasTexture(512, 64, (ctx) => {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 512, 64);
    ctx.fillStyle = '#1e8a5a';
    ctx.fillRect(0, 0, 512, 14);
    ctx.fillStyle = '#2a63c6';
    ctx.fillRect(0, 50, 512, 14);
    ctx.fillStyle = '#16324f';
    ctx.font = `bold 30px ${JP_FONT}`;
    ctx.textBaseline = 'middle';
    ctx.fillText('ヨルマート', 20, 33);
    ctx.fillStyle = '#e8541c';
    ctx.font = 'bold 30px sans-serif';
    ctx.fillText('24H', 420, 33);
  });
}

function createStationEntranceTexture(): THREE.CanvasTexture {
  return canvasTexture(256, 112, (ctx) => {
    ctx.fillStyle = '#e8eef6';
    ctx.fillRect(0, 0, 256, 112);
    ctx.fillStyle = '#9aa4b0';
    for (let i = 0; i < 5; i += 1) ctx.fillRect(30 + i * 45, 60, 14, 52);
    ctx.fillStyle = '#2c6fd1';
    ctx.fillRect(0, 0, 256, 18);
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold 13px ${JP_FONT}`;
    ctx.fillText('改札  TICKET GATES', 70, 13);
  });
}

function createStationSignTexture(): THREE.CanvasTexture {
  return canvasTexture(512, 80, (ctx) => {
    ctx.fillStyle = '#f6f6f2';
    ctx.fillRect(0, 0, 512, 80);
    ctx.fillStyle = '#1b6e3a';
    ctx.fillRect(0, 66, 512, 14);
    ctx.fillStyle = '#111';
    ctx.font = `bold 38px ${JP_FONT}`;
    ctx.textAlign = 'center';
    ctx.fillText('夜野駅', 256, 42);
    ctx.font = 'bold 14px sans-serif';
    ctx.fillText('YORUNO STATION', 256, 61);
  });
}

function createGlassDoorTexture(tint: string): THREE.CanvasTexture {
  return canvasTexture(128, 128, (ctx) => {
    ctx.fillStyle = tint;
    ctx.fillRect(0, 0, 128, 128);
    ctx.fillStyle = 'rgba(60,40,30,0.6)';
    ctx.fillRect(0, 0, 128, 6);
    ctx.fillRect(61, 0, 6, 128);
    ctx.fillRect(0, 0, 6, 128);
    ctx.fillRect(122, 0, 6, 128);
    ctx.fillStyle = 'rgba(120,90,70,0.35)';
    ctx.fillRect(10, 70, 46, 50);
  });
}

function createTextTexture(main: string, sub: string, fg: string, bg: string): THREE.CanvasTexture {
  return canvasTexture(256, 96, (ctx) => {
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, 256, 96);
    ctx.fillStyle = fg;
    ctx.textAlign = 'center';
    ctx.font = `bold 40px ${JP_FONT}`;
    ctx.fillText(main, 128, 50);
    ctx.font = 'bold 15px sans-serif';
    ctx.fillText(sub, 128, 80);
  });
}

function createStripeTexture(): THREE.CanvasTexture {
  return canvasTexture(128, 16, (ctx) => {
    ctx.fillStyle = '#f2c81e';
    ctx.fillRect(0, 0, 128, 16);
    ctx.fillStyle = '#111';
    for (let i = -16; i < 128; i += 16) {
      ctx.beginPath();
      ctx.moveTo(i, 16);
      ctx.lineTo(i + 8, 0);
      ctx.lineTo(i + 16, 0);
      ctx.lineTo(i + 8, 16);
      ctx.fill();
    }
  });
}

function createMoonTexture(): THREE.CanvasTexture {
  return canvasTexture(128, 128, (ctx) => {
    const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
    g.addColorStop(0, 'rgba(255,250,235,1)');
    g.addColorStop(0.28, 'rgba(240,236,220,1)');
    g.addColorStop(0.33, 'rgba(180,190,210,0.35)');
    g.addColorStop(1, 'rgba(100,120,160,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
    ctx.fillStyle = 'rgba(160,160,150,0.35)';
    ctx.beginPath();
    ctx.arc(56, 58, 7, 0, Math.PI * 2);
    ctx.arc(72, 72, 5, 0, Math.PI * 2);
    ctx.fill();
  });
}
